<template>
  <v-app>
    <v-main>
      <v-container class="employee-details text-left" fluid>
        <v-card class="hero-card px-6 py-5">
          <div class="hero-left">
            <v-avatar size="72" color="primary" variant="tonal" class="hero-avatar">
              {{ initials }}
            </v-avatar>
            <div>
              <div class="hero-name text-h5 d-flex align-center flex-wrap">
                <span class="mr-2">{{ fullName }}</span>
                <v-chip v-if="employee.status === 1" color="success" prepend-icon="mdi-check-circle-outline">
                  {{ $t("employeeDetails.hero.active") }}
                </v-chip>
                <v-chip v-else color="neutral" prepend-icon="mdi-pause-circle-outline">
                  {{ $t("employeeDetails.hero.inactive") }}
                </v-chip>
              </div>
              <div class="hero-meta">
                <div><strong>{{ $t("employeeDetails.hero.position") }}</strong> · {{ employee.position || '—' }}</div>
                <div><strong>{{ $t("employeeDetails.hero.company") }}</strong> · {{ employee.company_name || '—' }}</div>
                <div v-if="employee.role"><strong>{{ $t("employeeDetails.hero.role") }}</strong> · {{ employee.role }}</div>
                <div v-if="employee.manager_full_name"><strong>{{ $t("employeeDetails.hero.manager") }}</strong> · {{ employee.manager_full_name }}</div>
              </div>
            </div>
          </div>
          <div class="hero-actions">
            <v-btn color="primary" variant="outlined" prepend-icon="mdi-pencil-outline" @click="openEditDialog">
              {{ $t("app.actions.edit") }}
            </v-btn>
            <v-btn
              v-if="!employeeUserAccount"
              color="primary"
              variant="tonal"
              prepend-icon="mdi-account-plus-outline"
              @click="openCreateUserDialog"
              :disabled="!employee.email"
            >
              {{ $t("employeeDetails.hero.createUser") }}
            </v-btn>
            <v-btn
              v-else
              color="primary"
              variant="tonal"
              prepend-icon="mdi-account-key-outline"
              @click="openUpdateUserDialog"
            >
              {{ $t("employeeDetails.hero.changePassword") }}
            </v-btn>
            <v-btn
              color="tertiary"
              prepend-icon="mdi-beach"
              @click="openRequestDialog()"
              :disabled="!employee.company_id"
            >
              {{ $t("employeeDetails.hero.addHoliday") }}
            </v-btn>
          </div>
        </v-card>

        <v-row class="mt-4" dense>
          <!-- Left column -->
          <v-col cols="12" md="3" class="column">
            <v-card class="info-card">
              <v-card-title class="info-title">
                <v-icon size="15" class="mr-2">mdi-account-outline</v-icon>
                {{ $t("employeeDetails.sections.personal") }}
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <div
                  v-for="item in personalInfo"
                  :key="item.label"
                  class="info-row"
                >
                  <v-icon v-if="item.icon" size="16" class="mr-2">{{ item.icon }}</v-icon>
                  <strong>{{ item.label }}:</strong>
                  <a v-if="item.href" class="info-link" :href="item.href">{{ item.value }}</a>
                  <span v-else>{{ item.value }}</span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Middle column -->
          <v-col cols="12" md="6" class="column">
            <v-card class="info-card">
              <v-card-title class="info-title">
                <v-icon size="15" class="mr-2">mdi-briefcase-outline</v-icon>
                {{ $t("employeeDetails.sections.professional") }}
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <div
                  v-for="item in professionalInfo"
                  :key="item.label"
                  class="info-row"
                >
                  <v-icon v-if="item.icon" size="16" class="mr-2">{{ item.icon }}</v-icon>
                  <strong>{{ item.label }}:</strong>
                  <span>{{ item.value }}</span>
                </div>
              </v-card-text>
            </v-card>

            <v-card class="info-card">
              <v-card-title class="info-title d-flex align-center">
                <v-icon size="15" class="mr-2">mdi-beach</v-icon>
                {{ $t("employeeDetails.balance.title") }}
                <v-spacer></v-spacer>
                <v-btn size="small" variant="text" color="primary" prepend-icon="mdi-scale-balance" @click="openBalanceDialog('update')">
                  {{ $t("employeeDetails.balance.update") }}
                </v-btn>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <div class="balance-grid">
                  <div>
                    <div class="text-caption text-medium-emphasis">{{ $t("employeeDetails.balance.annual") }}</div>
                    <div class="text-h6 balance-value">{{ formatNumber(displayBalance.annual) }}</div>
                  </div>
                  <div>
                    <div class="text-caption text-medium-emphasis">{{ $t("employeeDetails.balance.totalAccrued") }}</div>
                    <div class="text-h6 balance-value">{{ formatNumber(displayBalance.totalAccrued) }}</div>
                  </div>
                  <div>
                    <div class="text-caption text-medium-emphasis">{{ $t("employeeDetails.balance.taken") }}</div>
                    <div class="text-h6 balance-value">{{ formatNumber(displayBalance.totalTaken) }}</div>
                  </div>
                  <div>
                    <div class="text-caption text-medium-emphasis">{{ $t("employeeDetails.balance.remaining") }}</div>
                    <div class="text-h6 balance-value text-success">{{ formatNumber(displayBalance.remaining) }}</div>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <NcrmRecordEditDialog
              v-model="timesheetFillDialog.open"
              :title="$t('employeeDetails.timesheet.title')"
              :loading="timesheetFillDialog.loading"
              :save-label="$t('employeeDetails.timesheet.fill')"
              max-width="480"
              @save="confirmTimesheetFill"
            >
                  <p class="text-body-2 mb-3">
                    {{ $t("employeeDetails.timesheet.description") }}
                  </p>
                  <v-alert
                    v-if="timesheetFillDialog.error"
                    type="error"
                    density="compact"
                    class="mb-3"
                  >
                    {{ timesheetFillDialog.error }}
                  </v-alert>
                  <v-menu
                    :z-index="2710"
                    v-model="timesheetFillRangeMenu"
                    :close-on-content-click="false"
                    max-width="360"
                    transition="scale-transition"
                  >
                    <template #activator="{ props: menuProps }">
                      <div class="d-flex ga-2 flex-wrap w-100" v-bind="menuProps">
                        <v-text-field
                          class="flex-grow-1"
                          :label="$t('employeeDetails.timesheet.from')"
                          readonly
                          :model-value="timesheetFillRangeDisplay.start"
                          @click.stop="handleTimesheetFillControlClick('start', $event, menuProps)"
                          @mousedown="handleTimesheetFillPointerDown('start')"
                          @focus="handleTimesheetFillFieldFocus('start')"
                          @keydown.enter.prevent="handleTimesheetFillKeyboardOpen('start')"
                          @keydown.space.prevent="handleTimesheetFillKeyboardOpen('start')"
                          @click:control.stop="handleTimesheetFillControlClick('start', $event, menuProps)"
                        ></v-text-field>
                        <v-text-field
                          class="flex-grow-1"
                          :label="$t('employeeDetails.timesheet.to')"
                          readonly
                          :model-value="timesheetFillRangeDisplay.end"
                          @click.stop="handleTimesheetFillControlClick('end', $event, menuProps)"
                          @mousedown="handleTimesheetFillPointerDown('end')"
                          @focus="handleTimesheetFillFieldFocus('end')"
                          @keydown.enter.prevent="handleTimesheetFillKeyboardOpen('end')"
                          @keydown.space.prevent="handleTimesheetFillKeyboardOpen('end')"
                          @click:control.stop="handleTimesheetFillControlClick('end', $event, menuProps)"
                        ></v-text-field>
                      </div>
                    </template>
                    <v-card>
                      <v-date-picker
                        :model-value="timesheetFillRangeDateModel"
                        color="primary"
                        :first-day-of-week="1"
                        show-adjacent-months
                        range
                        @update:model-value="onTimesheetFillRangeChange"
                      ></v-date-picker>
                      <v-card-actions class="justify-end">
                        <v-btn variant="text" @click="timesheetFillRangeMenu = false">{{ $t("app.actions.cancel") }}</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-menu>
            </NcrmRecordEditDialog>
          </v-col>

          <!-- Right column -->
          <v-col cols="12" md="3" class="column">
            <v-card class="info-card timesheet-card">
              <v-card-title class="info-title d-flex align-center">
                <v-icon size="15" class="mr-2">mdi-clock-outline</v-icon>
                {{ $t("employeeDetails.timesheet.cardTitle") }}
                <v-chip
                  size="x-small"
                  class="ml-2"
                  :color="timesheetSettings.use_automatic ? 'primary' : 'neutral'"
                >
                  {{ timesheetSettings.use_automatic ? $t("employeeDetails.timesheet.automatic") : $t("employeeDetails.timesheet.manual") }}
                </v-chip>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text class="timesheet-content">
                <v-progress-linear
                  v-if="timesheetLoading"
                  indeterminate
                  color="primary"
                  class="mb-3"
                ></v-progress-linear>

                <v-alert
                  v-if="timesheetNotice && !timesheetDrawer"
                  :type="timesheetNotice.type || 'info'"
                  density="compact"
                  class="mb-3"
                >
                  {{ timesheetNotice.text }}
                </v-alert>

                <EmployeeTimesheetSettingsSummary
                  :settings="timesheetSettings"
                  :idle-minutes="smartTrackerIdleMinutes"
                  :prompt-minutes="smartTrackerPromptMinutes"
                />

                <div class="timesheet-actions">
                  <v-btn
                    color="primary"
                    variant="outlined"
                    prepend-icon="mdi-pencil-outline"
                    :disabled="timesheetLoading"
                    @click="timesheetDrawer = true"
                  >
                    {{ $t("employeeDetails.timesheet.editSettings") }}
                  </v-btn>
                  <v-btn
                    color="primary"
                    variant="tonal"
                    class="timesheet-fill-btn"
                    :disabled="timesheetLoading || timesheetSaving"
                    @click="openTimesheetFillDialog"
                  >
                    {{ $t("employeeDetails.timesheet.fillMonth") }}
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>

            <NcrmRecordEditDialog
              v-model="timesheetDrawer"
              :title="$t('employeeDetails.timesheet.drawerTitle')"
              :subtitle="$t('employeeDetails.timesheet.saveHint')"
              :save-label="$t('employeeDetails.timesheet.drawerDone')"
              :loading="timesheetSaving"
              hide-cancel
              @save="closeTimesheetDrawer"
            >
              <v-alert
                v-if="timesheetNotice"
                :type="timesheetNotice.type || 'info'"
                density="compact"
                class="mb-3"
              >
                {{ timesheetNotice.text }}
              </v-alert>

            <div class="timesheet-section">
              <div class="timesheet-section__header">
                <div class="text-subtitle-2 ncrm-text-strong">
                  {{ $t("employeeDetails.timesheet.automaticSectionTitle") }}
                </div>
                <div class="timesheet-section__hint">
                  {{ $t("employeeDetails.timesheet.automaticSectionHint") }}
                </div>
              </div>

              <div class="timesheet-switch-wrapper">
                <v-switch
                  v-model="timesheetSettings.use_automatic"
                  inset
                  color="primary"
                  hide-details
                  density="compact"
                  :disabled="timesheetLoading || timesheetSaving"
                >
                  <template #label>
                    <div class="timesheet-switch-label">
                      <span class="timesheet-switch-title">{{ $t("employeeDetails.timesheet.switchLabel") }}</span>
                    </div>
                  </template>
                </v-switch>
              </div>

              <div class="timesheet-time-grid">
                <v-text-field
                  :label="$t('employeeDetails.timesheet.workdayStart')"
                  type="time"
                  v-model="timesheetSettings.workday_start"
                  :disabled="timesheetLoading || timesheetSaving"
                  hide-details="auto"
                  prepend-inner-icon="mdi-clock-time-five-outline"
                ></v-text-field>
                <v-text-field
                  :label="$t('employeeDetails.timesheet.workdayEnd')"
                  type="time"
                  v-model="timesheetSettings.workday_end"
                  :disabled="timesheetLoading || timesheetSaving"
                  hide-details="auto"
                  prepend-inner-icon="mdi-clock-time-eleven-outline"
                ></v-text-field>
                <v-text-field
                  :label="$t('employeeDetails.timesheet.breakStart')"
                  type="time"
                  v-model="timesheetSettings.break_start"
                  :disabled="timesheetLoading || timesheetSaving"
                  hide-details="auto"
                  prepend-inner-icon="mdi-coffee"
                ></v-text-field>
                <v-text-field
                  :label="$t('employeeDetails.timesheet.breakEnd')"
                  type="time"
                  v-model="timesheetSettings.break_end"
                  :disabled="timesheetLoading || timesheetSaving"
                  hide-details="auto"
                  prepend-inner-icon="mdi-coffee-to-go"
                ></v-text-field>
              </div>
            </div>

            <v-divider class="my-4"></v-divider>

            <div class="timesheet-section timesheet-smart-tracker-settings">
              <div class="timesheet-section__header">
                <div class="text-subtitle-2 ncrm-text-strong">
                  {{ $t("employeeDetails.timesheet.smartTracker.title") }}
                </div>
                <div class="timesheet-section__hint">
                  {{ $t("employeeDetails.timesheet.smartTracker.sectionHint") }}
                </div>
              </div>
              <v-switch
                v-model="timesheetSettings.smart_tracker_enabled"
                inset
                color="primary"
                hide-details="auto"
                density="compact"
                :disabled="timesheetLoading || timesheetSaving"
                :label="$t('employeeDetails.timesheet.smartTracker.enabledLabel')"
                class="timesheet-switch--compact-label"
              />
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    :label="$t('employeeDetails.timesheet.smartTracker.idleMinutes')"
                    type="number"
                    min="0.5"
                    max="120"
                    step="0.5"
                    :suffix="$t('employeeDetails.timesheet.smartTracker.minutesSuffix')"
                    v-model.number="smartTrackerIdleMinutes"
                    :disabled="timesheetLoading || timesheetSaving"
                    hide-details="auto"
                    prepend-inner-icon="mdi-timer-sand"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    :label="$t('employeeDetails.timesheet.smartTracker.promptMinutes')"
                    type="number"
                    min="0.2"
                    max="60"
                    step="0.1"
                    :suffix="$t('employeeDetails.timesheet.smartTracker.minutesSuffix')"
                    v-model.number="smartTrackerPromptMinutes"
                    :disabled="timesheetLoading || timesheetSaving"
                    hide-details="auto"
                    prepend-inner-icon="mdi-timer-outline"
                  />
                </v-col>
              </v-row>
              <div class="timesheet-section__hint">
                {{ $t("employeeDetails.timesheet.smartTracker.minutesHint") }}
              </div>
              <v-switch
                v-model="timesheetSettings.smart_tracker_auto_label_enabled"
                inset
                color="primary"
                hide-details="auto"
                density="compact"
                :disabled="timesheetLoading || timesheetSaving"
                :label="$t('employeeDetails.timesheet.smartTracker.autoLabelEnabled')"
                class="timesheet-switch--compact-label"
              />
            </div>
            </NcrmRecordEditDialog>

          </v-col>
        </v-row>

        <v-row v-if="showHrTimesheet" class="mt-4">
          <v-col cols="12">
            <EmployeeTimesheetHr
              :employee-id="employeeId"
              :title="$t('employeeDetails.timesheet.dailyTitle')"
              :can-manage="showHrTimesheet"
              :employee-name="fullName"
            />
          </v-col>
        </v-row>

        <v-row v-if="subordinateEmployees.length" class="mt-4">
          <v-col cols="12">
            <v-card class="info-card subordinate-section">
              <v-card-title class="info-title">
                <v-icon size="15" class="mr-2">mdi-account-group-outline</v-icon>
                {{ $t("employeeDetails.subordinate.title") }}
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="4" v-for="sub in subordinateEmployees" :key="sub.id">
                    <v-card
                      class="subordinate-card"
                      @click="openSubordinateDetails(sub.id)"
                    >
                      <div class="d-flex align-center mb-3">
                        <v-avatar color="primary" variant="tonal" class="mr-3">
                          {{ (sub.first_name?.[0] || '') + (sub.last_name?.[0] || '') }}
                        </v-avatar>
                        <div>
                          <button
                            type="button"
                            class="subordinate-card__name text-subtitle-1 font-weight-medium"
                            :aria-label="`${$t('employeeDetails.subordinate.viewProfile')}: ${sub.first_name} ${sub.last_name}`"
                            @click.stop="openSubordinateDetails(sub.id)"
                          >
                            {{ sub.first_name }} {{ sub.last_name }}
                          </button>
                          <div class="text-caption text-medium-emphasis">{{ sub.position || '—' }}</div>
                        </div>
                      </div>
                      <div class="text-caption mb-1">
                        {{ $t("employeeDetails.subordinate.pending") }}: {{ subordinatePendingRequests.filter(r => r.employee_id === sub.id).length }}
                      </div>
                      <div v-if="canViewSubordinateBalances" class="text-caption text-medium-emphasis mb-3">
                        {{ $t("employeeDetails.subordinate.remaining") }}: {{ getSubordinateRemainingDisplay(sub.id) }}
                      </div>
                      <div class="d-flex justify-space-between align-center">
                        <span @click.stop>
                          <v-btn
                            size="small"
                            variant="outlined"
                            color="primary"
                            @click="goToSubordinateApprovals(sub.id)"
                            :disabled="!subordinatePendingRequests.some(r => r.employee_id === sub.id)"
                          >
                            {{ $t("employeeDetails.subordinate.approvePending") }}
                          </v-btn>
                        </span>
                        <v-icon size="20" class="subordinate-card__chevron">mdi-chevron-right</v-icon>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="mt-4">
          <v-col cols="12" md="6" lg="7">
            <v-card class="info-card">
              <v-card-title class="info-title d-flex align-center">
                <v-icon size="15" class="mr-2">mdi-history</v-icon>
                {{ $t("employeeDetails.history.title") }}
                <v-spacer></v-spacer>
                <v-btn
                  v-if="sortedHolidayRequests.length"
                  size="small"
                  variant="text"
                  color="primary"
                  @click="toggleShowAllHolidayRequests"
                >
                  {{ showAllHolidayRequests ? $t("employeeDetails.history.hide") : $t("employeeDetails.history.showAll") }}
                </v-btn>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text class="vacation-requests-list">
                <div v-if="!sortedHolidayRequests.length" class="history-empty">
                  <v-icon size="22" class="history-empty__icon">mdi-beach</v-icon>
                  <div class="text-body-2 text-medium-emphasis">{{ $t("employeeDetails.history.none") }}</div>
                  <v-btn
                    size="small"
                    variant="outlined"
                    color="primary"
                    prepend-icon="mdi-plus"
                    :disabled="!employee.company_id"
                    @click="openRequestDialog()"
                  >
                    {{ $t("employeeDetails.hero.addHoliday") }}
                  </v-btn>
                </div>
                <template v-else>
                  <div
                    v-for="request in visibleHolidayRequestsOverview"
                    :key="request.id"
                    :class="['request-row', isPastHolidayRequest(request) ? 'request-row--past' : 'request-row--upcoming']"
                  >
                    <div class="request-row__header">
                      <div class="request-row__title">
                        <v-chip size="x-small" :color="statusChipColor(request.status, request.pending_action)">
                          {{ statusLabel(request.status, request.pending_action) }}
                        </v-chip>
                        <span class="ml-2">
                          {{ request.holiday_type_name || lookupHolidayType(request.holiday_type_id) || $t("employeeDetails.history.unknownType") }}
                        </span>
                      </div>
                      <div class="request-row__meta">
                        <span class="text-caption mr-2">{{ $t("employeeDetails.history.days", { days: formatNumber(getWorkingDays(request)) }) }}</span>
                        <div v-if="!companyPdfDisabled" class="request-row__downloads">
                          <v-tooltip :text="$t('employeeHolidays.history.downloadRequest')">
                            <template #activator="{ props }">
                              <v-btn
                                icon
                                size="small"
                                variant="text"
                                :loading="pdfDownloadLoadingId === `request-${request.id}`"
                                :aria-label="$t('employeeHolidays.history.downloadRequest')"
                                v-bind="props"
                                @click="downloadPdf(request, 'request')"
                              >
                                <v-icon size="18">mdi-download-outline</v-icon>
                              </v-btn>
                            </template>
                          </v-tooltip>
                          <v-tooltip v-if="request.status === 'approved'" :text="$t('employeeHolidays.history.downloadOrder')">
                            <template #activator="{ props }">
                              <v-btn
                                icon
                                size="small"
                                variant="text"
                                color="primary"
                                :loading="pdfDownloadLoadingId === `order-${request.id}`"
                                :aria-label="$t('employeeHolidays.history.downloadOrder')"
                                v-bind="props"
                                @click="downloadPdf(request, 'order')"
                              >
                                <v-icon size="18">mdi-file-download-outline</v-icon>
                              </v-btn>
                            </template>
                          </v-tooltip>
                        </div>
                      </div>
                    </div>
                    <div class="text-caption mb-1">
                      {{ formatDisplayDate(request.start_date) }} – {{ formatDisplayDate(request.end_date) }}
                    </div>
                <div v-if="request.pending_action === 'cancel'" class="text-caption text-error mb-1">
                  {{ $t("employeeDetails.history.statusPendingCancel") }}
                </div>
                <div v-if="request.notes" class="text-caption mb-2">{{ request.notes }}</div>
                <div class="request-row__actions">
                  <v-btn
                    size="small"
                    variant="tonal"
                    color="success"
                    @click="approveRequest(request)"
                    :disabled="!canManageRequests || request.status === 'approved'"
                  >
                    {{ $t("employeeDetails.history.actions.approve") }}
                  </v-btn>
                  <v-menu location="bottom end">
                    <template #activator="{ props: menuProps }">
                      <v-btn
                        icon="mdi-dots-vertical"
                        size="small"
                        variant="text"
                        v-bind="menuProps"
                        :aria-label="$t('employeeDetails.history.actions.more')"
                      ></v-btn>
                    </template>
                    <v-list density="compact">
                      <v-list-item
                        prepend-icon="mdi-pencil-outline"
                        :title="$t('employeeDetails.history.actions.edit')"
                        :disabled="request.status === 'approved' && !canEditApprovedRequest"
                        @click="openRequestDialog(request)"
                      ></v-list-item>
                      <v-list-item
                        prepend-icon="mdi-cancel"
                        :title="$t('employeeDetails.history.actions.cancel')"
                        :disabled="!canManageRequests || request.status === 'cancelled'"
                        @click="cancelRequest(request)"
                      ></v-list-item>
                      <v-list-item
                        base-color="error"
                        prepend-icon="mdi-close-circle-outline"
                        :title="$t('employeeDetails.history.actions.reject')"
                        :disabled="!canManageRequests || request.status === 'rejected'"
                        @click="rejectRequest(request)"
                      ></v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </div>
              <div class="d-flex justify-center mt-1">
                <v-btn
                  v-if="hasMoreHolidayRequests"
                  size="small"
                      variant="text"
                      color="primary"
                      @click="showMoreHolidayRequests"
                    >
                      {{ $t("employeeDetails.history.showMore") }}
                    </v-btn>
                  </div>
                </template>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="6" lg="5">
            <v-card class="info-card calendar-card">
              <v-card-title class="info-title d-flex align-center">
                <v-icon size="15" class="mr-2">mdi-calendar-month-outline</v-icon>
                {{ $t("employeeDetails.calendar.title") }}
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <div class="calendar-wrapper">
                  <VCalendar
                    class="vacation-calendar"
                    type="month"
                    :model-value="overviewCalendarSelection"
                    @update:model-value="onOverviewCalendarModelUpdate"
                    :events="overviewCalendarEvents"
                    :event-color="eventColorByEvent"
                    color="primary"
                    :weekdays="[1, 2, 3, 4, 5, 6, 0]"
                  >
                    <template #event="{ event }">
                      <div
                        v-if="event?.meta?.highlightKind"
                        class="calendar-day-overlay"
                        :class="`calendar-day-overlay--${event.meta.highlightKind}`"
                      ></div>
                      <v-chip
                        class="calendar-event-chip"
                        :color="event.color"
                        label
                      >
                        <span class="calendar-event-chip__dot" :class="`bg-${event.color}`"></span>
                        {{ event.title }}
                      </v-chip>
                    </template>
                  </VCalendar>
                </div>
                <div class="calendar-legend mt-3">
                  <div class="legend-item">
                    <span class="legend-dot approved" />
                    {{ $t("employeeDetails.calendar.legend.approved") }}
                  </div>
                  <div class="legend-item">
                    <span class="legend-dot pending" />
                    {{ $t("employeeDetails.calendar.legend.pending") }}
                  </div>
                  <div class="legend-item">
                    <span class="legend-dot non-working" />
                    {{ $t("employeeDetails.calendar.legend.nonWorking") }}
                  </div>
                  <div class="legend-item">
                    <span class="legend-dot short-day" />
                    {{ $t("employeeDetails.calendar.legend.shortDay") }}
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Dialogs for contacts and forms -->
        <NcrmRecordEditDialog
          v-model="editDialog"
          :title="$t('employeeDetails.form.editTitle')"
          :loading="editEmployeeLoading"
          :save-label="$t('employeeDetails.form.save')"
          max-width="640"
          @save="updateEmployee"
        >
          <v-alert v-if="editEmployeeError" type="error" class="mb-3">{{ editEmployeeError }}</v-alert>
              <v-form ref="editEmployeeFormRef" @submit.prevent>
                <v-container class="pa-0">
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <v-select
                        :menu-props="drawerMenuProps"
                        :label="`${$t('employeeDetails.form.company')} *`"
                        :items="companyOptions"
                        item-title="title"
                        item-value="value"
                        v-model="editEmployee.company_id"
                        :rules="[rules.required]"
                        required
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :label="`${$t('employeeDetails.form.firstName')} *`"
                        v-model="editEmployee.first_name"
                        :rules="[rules.required]"
                        required
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :label="`${$t('employeeDetails.form.lastName')} *`"
                        v-model="editEmployee.last_name"
                        :rules="[rules.required]"
                        required
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :label="$t('employeeDetails.form.birthDate')"
                        type="date"
                        v-model="editEmployee.birth_date"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :label="$t('employeeDetails.form.hireDate')"
                        type="date"
                        v-model="editEmployee.hire_date"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :label="$t('employeeDetails.form.releaseDate')"
                        type="date"
                        v-model="editEmployee.release_date"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        :menu-props="drawerMenuProps"
                        :label="$t('employeeDetails.form.status')"
                        :items="employeeStatusOptions"
                        item-title="title"
                        item-value="value"
                        v-model="editEmployee.status"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :label="$t('employeeDetails.form.phone')"
                        v-model="editEmployee.phone"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :label="$t('employeeDetails.form.email')"
                        v-model="editEmployee.email"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :label="$t('employeeDetails.form.position')"
                        v-model="editEmployee.position"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        :menu-props="drawerMenuProps"
                        :label="$t('employeeDetails.form.role')"
                        :items="employeeRoleOptions"
                        item-title="title"
                        item-value="value"
                        v-model="editEmployee.role"
                        clearable
                        hide-details="auto"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        :menu-props="drawerMenuProps"
                        :label="$t('employeeDetails.form.manager')"
                        :items="managerOptions"
                        item-title="title"
                        item-value="value"
                        v-model="editEmployee.id_manager"
                        clearable
                        :disabled="!editEmployee.company_id"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :label="$t('employeeDetails.form.department')"
                        v-model="editEmployee.department"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :label="$t('employeeDetails.form.salary')"
                        type="number"
                        v-model="editEmployee.salary"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        :menu-props="drawerMenuProps"
                        :label="$t('employeeDetails.form.contractType')"
                        :items="contractTypeOptions"
                        item-title="title"
                        item-value="value"
                        v-model="editEmployee.type_of_contract"
                        clearable
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :label="$t('employeeDetails.form.address')"
                        v-model="editEmployee.address"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :label="$t('employeeDetails.form.cnp')"
                        v-model="editEmployee.cnp"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        :menu-props="drawerMenuProps"
                        :label="$t('employeeDetails.form.gender')"
                        :items="genderOptions"
                        item-title="title"
                        item-value="value"
                        v-model="editEmployee.gender"
                        clearable
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-combobox
                        :menu-props="drawerMenuProps"
                        :label="$t('employeeDetails.form.nationality')"
                        :items="nationalityOptions"
                        item-title="title"
                        item-value="value"
                        v-model="editEmployee.nationality"
                        clearable
                        hide-selected
                      ></v-combobox>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        :menu-props="drawerMenuProps"
                        :label="$t('employeeDetails.form.maritalStatus')"
                        :items="maritalStatusOptions"
                        item-title="title"
                        item-value="value"
                        v-model="editEmployee.marital_status"
                        clearable
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-container>
              </v-form>
        </NcrmRecordEditDialog>

        <HolidayRequestPopUp
          v-model="requestDialog"
          :loading="requestDialogLoading"
          :request-form="requestForm"
          :holiday-type-options="holidayTypeOptions"
          :status-options="statusOptions"
          :request-range-menu="requestRangeMenu"
          :range-date-model="rangeDateModel"
          :allowed-calendar-dates="allowedCalendarDates"
          :calendar-events="calendarEvents"
          :on-calendar-range-change="onCalendarRangeChange"
          :on-range-pointer-down="handleRangePointerDown"
          :on-range-field-focus="handleRangeFieldFocus"
          :on-range-keyboard-open="handleRangeKeyboardOpen"
          :on-range-control-click="handleRangeControlClick"
          @update:request-range-menu="(value) => (requestRangeMenu = value)"
          @cancel="requestDialog = false"
          @submit="submitHolidayRequest"
        />

        <v-dialog v-model="downloadPromptDialog" max-width="420">
          <v-card>
            <v-card-title>{{ $t("employeeDetails.request.downloadPromptTitle") }}</v-card-title>
            <v-card-text>{{ $t("employeeDetails.request.downloadPromptText") }}</v-card-text>
            <v-card-actions class="justify-end">
              <v-btn variant="text" @click="downloadPromptDialog = false">{{ $t("employeeDetails.request.downloadNo") }}</v-btn>
              <v-btn color="primary" :loading="pdfDownloadLoadingId !== null" @click="confirmDownloadAfterSave">
                {{ $t("employeeDetails.request.downloadYes") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-snackbar
          v-model="feedbackSnack.open"
          :timeout="3500"
          :z-index="2800"
          :color="feedbackSnack.color"
          location="bottom right"
        >
          {{ feedbackSnack.text }}
        </v-snackbar>

        <NcrmRecordEditDialog
          v-model="createUserDialog"
          :title="userDialogTitle"
          :loading="userDialogLoading"
          :save-label="userDialogSubmitLabel"
          :save-disabled="inviteLoading"
          :cancel-disabled="inviteLoading || userDialogLoading"
          :close-disabled="inviteLoading || userDialogLoading"
          :extra-action-label="inviteButtonLabel"
          :extra-action-disabled="inviteLoading || userDialogLoading || !canSendInvite"
          max-width="520"
          @save="handleUserFormSubmit"
          @extra-action="sendUserInvite"
        >
          <v-alert v-if="userDialogError" type="error" class="mb-3">{{ userDialogError }}</v-alert>
              <v-form @submit.prevent>
                <v-container>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :label="$t('employeeDetails.userDialog.email')"
                        v-model="userForm.email"
                        type="email"
                        :rules="[rules.required]"
                        :required="userDialogMode === 'create'"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :label="$t('employeeDetails.userDialog.password')"
                        v-model="userForm.password"
                        type="password"
                        :hint="userDialogMode === 'edit' ? $t('employeeDetails.userDialog.passwordHintEdit') : $t('employeeDetails.userDialog.passwordHintCreate')"
                        :persistent-hint="userDialogMode === 'edit'"
                        :required="userDialogMode === 'create'"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :label="$t('employeeDetails.userDialog.name')"
                        v-model="userForm.name"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :label="$t('employeeDetails.userDialog.role')"
                        v-model="userForm.role"
                        :rules="[rules.required]"
                        required
                        :readonly="userDialogMode === 'create'"
                        :bg-color="userDialogMode === 'create' ? 'grey-lighten-4' : undefined"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-container>
              </v-form>
        </NcrmRecordEditDialog>

        <v-dialog v-model="timesheetAutoPrompt" max-width="420px">
          <v-card>
            <v-card-title class="info-title">
              {{ $t("employeeDetails.timesheetAutoPrompt.title") }}
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              {{ $t("employeeDetails.timesheetAutoPrompt.description") }}
            </v-card-text>
            <v-card-actions>
              <v-btn variant="text" @click="dismissTimesheetAutoPrompt">{{ $t("employeeDetails.timesheetAutoPrompt.decline") }}</v-btn>
              <v-spacer></v-spacer>
              <v-btn color="primary" :loading="timesheetSaving" @click="confirmTimesheetAutoPrompt">
                {{ $t("employeeDetails.timesheetAutoPrompt.confirm") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <NcrmRecordEditDialog
          v-model="balanceDialog"
          :title="balanceDialogMode === 'edit' ? $t('employeeDetails.balanceDialog.editTitle') : $t('employeeDetails.balanceDialog.updateTitle')"
          :loading="balanceDialogLoading"
          :save-label="$t('employeeDetails.balanceDialog.save')"
          max-width="520"
          @save="submitBalance"
        >
          <v-alert v-if="balanceDialogError" type="error" class="mb-3">{{ balanceDialogError }}</v-alert>
              <v-form @submit.prevent>
                <v-container>
                  <v-row>
                    <v-col cols="12" md="6"><v-text-field :label="$t('employeeDetails.balanceDialog.annual')" type="number" v-model="balanceForm.annual_leave_days"></v-text-field></v-col>
                    <v-col cols="12" md="6"><v-text-field :label="$t('employeeDetails.balanceDialog.carryover')" type="number" v-model="balanceForm.carryover_days"></v-text-field></v-col>
                    <v-col cols="12" md="6"><v-text-field :label="$t('employeeDetails.balanceDialog.accrualStart')" type="date" v-model="balanceForm.accrual_start_date"></v-text-field></v-col>
                    <v-col cols="12" md="6"><v-text-field :label="$t('employeeDetails.balanceDialog.accrualFinish')" type="date" v-model="balanceForm.accrual_finish_date"></v-text-field></v-col>
                    <v-col cols="12" md="6"><v-text-field :label="$t('employeeDetails.balanceDialog.totalAccrued')" type="number" v-model="balanceForm.total_accrued_days" readonly></v-text-field></v-col>
                    <v-col cols="12" md="6"><v-text-field :label="$t('employeeDetails.balanceDialog.totalTaken')" type="number" v-model="balanceForm.total_taken_days"></v-text-field></v-col>
                    <v-col cols="12" md="6"><v-text-field :label="$t('employeeDetails.balanceDialog.remaining')" type="number" v-model="balanceForm.remaining_days"></v-text-field></v-col>
                    <v-col cols="12"><v-textarea :label="$t('employeeDetails.balanceDialog.notes')" v-model="balanceForm.notes" rows="2"></v-textarea></v-col>
                  </v-row>
                </v-container>
              </v-form>
        </NcrmRecordEditDialog>

      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import axios from "axios";
import { defineComponent, ref, onMounted, onBeforeUnmount, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { VCalendar } from "vuetify/labs/VCalendar";
import { computeWorkingDays as computeWorkingDaysUtil, normalizeWorkingDate } from "@/Utile/computeWorkingDays.mjs";
import {
  toNumber,
  formatNumber,
  formatDate,
  formatDisplayDate,
  formatDisplayAmount,
  formatDateTime,
  formatAsIsoDate,
  normalizeDateString,
  toDateObject,
  computeAccruedUntilYearEnd,
} from "@/Utile/holidayFormatting.mjs";
import { useRoute, useRouter } from "vue-router";
import { decideHighlight, buildShortDaySetFromNonWorking } from "@/Utile/calendarHighlight.mjs";
import { isInviteReady } from "@/components/userAccount/inviteUtils.mjs";
import { expandRoleAliases, normalizeRoleKey } from "@/Utile/roleHelpers.mjs";
import {
  DEFAULT_TIMESHEET_SETTINGS,
  createDefaultTimesheetSettings,
  normalizeIntegerInRange,
  normalizeDecimalInRange,
  secondsToMinutes,
  toUiTimesheetSettings,
  toServerTimesheetPayload,
} from "@/Utile/timesheetSettings.mjs";
import {
  createEmptyEmployeeForm,
  normalizeDateForInput,
  formatNumericField,
  parseNumeric,
  sanitizeRequiredText,
  sanitizeText,
} from "@/Utile/employeeForm.mjs";
import {
  isAnnualLeaveType,
  hasApprovedAnnualLeaveOverlap,
  buildBlockedAnnualLeaveDays,
} from "@/Utile/employeeLeave.mjs";
import { normalizeRangeSelection, buildDefaultFillRange } from "@/Utile/calendarRange.mjs";
import HolidayRequestPopUp from "./holiday/HolidayRequestPopUp.vue";
import EmployeeTimesheetHr from "./EmployeeTimesheetHr.vue";
import EmployeeTimesheetSettingsSummary from "./employee/EmployeeTimesheetSettingsSummary.vue";
import NcrmRecordEditDialog from "./ncrm/shared/NcrmRecordEditDialog.vue";
import { buildEmployeeFormOptions } from "@/Utile/employeeFormOptions.mjs";

const APPROVER_ROLES = ["admin", "miniadmin", "vacation-manager", "vacation-director", "vacation-hr"];
const DISPLAYED_CALENDAR_STATUSES = new Set(["pending", "approved"]);
const OVERVIEW_INITIAL_VISIBLE_REQUESTS = 4;
const OVERVIEW_LOAD_MORE_STEP = 10;

export default defineComponent({
  name: "EmployeeDetails",
  components: {
    VCalendar,
    HolidayRequestPopUp,
    EmployeeTimesheetHr,
    EmployeeTimesheetSettingsSummary,
    NcrmRecordEditDialog,
  },
  props: {
    employeeId: {
      type: [Number, String],
      default: null,
    },
    companyId: {
      type: [Number, String],
      default: null,
    },
    inlineNavigation: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const route = useRoute();
    const router = useRouter();

    const parseEmployeeId = (value) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
    };

    const resolveInitialEmployeeId = () => {
      const propId = parseEmployeeId(props.employeeId);
      if (propId) return propId;
      const routeId = parseEmployeeId(route.params.id);
      return routeId;
    };

    const employeeId = ref(resolveInitialEmployeeId());
    const employee = ref({});
    const employeesList = ref([]);
    const companiesList = ref([]);
    const holidayTypes = ref([]);
    const holidayBalance = ref(null);
    const holidayRequests = ref([]);
    const nonWorkingDays = ref([]);
    const loadingRequests = ref(false);

    const timesheetSettings = ref(createDefaultTimesheetSettings());
    const smartTrackerIdleMinutes = computed({
      get() {
        return secondsToMinutes(
          normalizeIntegerInRange(
            timesheetSettings.value.smart_tracker_idle_seconds,
            DEFAULT_TIMESHEET_SETTINGS.smart_tracker_idle_seconds,
            30,
            7200
          )
        );
      },
      set(value) {
        const minutes = normalizeDecimalInRange(
          value,
          secondsToMinutes(DEFAULT_TIMESHEET_SETTINGS.smart_tracker_idle_seconds),
          secondsToMinutes(30),
          secondsToMinutes(7200)
        );
        timesheetSettings.value.smart_tracker_idle_seconds = normalizeIntegerInRange(
          Math.round(minutes * 60),
          DEFAULT_TIMESHEET_SETTINGS.smart_tracker_idle_seconds,
          30,
          7200
        );
      },
    });
    const smartTrackerPromptMinutes = computed({
      get() {
        return secondsToMinutes(
          normalizeIntegerInRange(
            timesheetSettings.value.smart_tracker_prompt_seconds,
            DEFAULT_TIMESHEET_SETTINGS.smart_tracker_prompt_seconds,
            10,
            3600
          )
        );
      },
      set(value) {
        const minutes = normalizeDecimalInRange(
          value,
          secondsToMinutes(DEFAULT_TIMESHEET_SETTINGS.smart_tracker_prompt_seconds),
          secondsToMinutes(10),
          secondsToMinutes(3600)
        );
        timesheetSettings.value.smart_tracker_prompt_seconds = normalizeIntegerInRange(
          Math.round(minutes * 60),
          DEFAULT_TIMESHEET_SETTINGS.smart_tracker_prompt_seconds,
          10,
          3600
        );
      },
    });
    const timesheetLoading = ref(false);
    const timesheetSaving = ref(false);
    const timesheetDrawer = ref(false);
    const timesheetNotice = ref(null);
    const timesheetAutosaveEnabled = ref(false);
    const timesheetAutosaveApplying = ref(false);
    const timesheetAutosaveTimer = ref(null);
    const timesheetAutosaveSignature = ref("");
    const timesheetFillDialog = ref({
      open: false,
      loading: false,
      error: null,
      start_date: "",
      end_date: "",
    });
    const timesheetFillRangeMenu = ref(false);
    const timesheetFillActiveEdge = ref("start");

    const clearTimesheetAutosaveTimer = () => {
      if (timesheetAutosaveTimer.value) {
        clearTimeout(timesheetAutosaveTimer.value);
        timesheetAutosaveTimer.value = null;
      }
    };

    const buildTimesheetPayloadSnapshot = (settings = timesheetSettings.value) => {
      const payload = toServerTimesheetPayload(settings);
      return {
        payload,
        signature: JSON.stringify(payload),
      };
    };

    const showHrTimesheet = computed(() =>
      ["hr", "vacation-hr", "admin", "miniadmin"].some((roleKey) => userRoleSet.value.has(roleKey))
    );

    const editDialog = ref(false);
    const editEmployee = ref(createEmptyEmployeeForm());

    const requestDialog = ref(false);
    const requestDialogLoading = ref(false);
    const companyPdfDisabled = ref(false);
    const pdfDownloadLoadingId = ref(null);
    const downloadPromptDialog = ref(false);
    const lastSavedRequestId = ref(null);
    // Listele peste drawer (`NcrmRecordEditDialog` este la z-index 2600): același `zIndex` ca în celelalte drawere.
    const drawerMenuProps = { maxHeight: 320, zIndex: 2710 };
    const feedbackSnack = ref({
      open: false,
      color: "success",
      text: "",
    });
    const notify = (text, color = "success") => {
      feedbackSnack.value = { open: true, color, text };
    };
    const editEmployeeLoading = ref(false);
    const editEmployeeError = ref("");
    const editEmployeeFormRef = ref(null);
    const balanceDialogError = ref("");
    const userDialogError = ref("");
    const requestForm = ref({
      holiday_type_id: null,
      start_date: "",
      end_date: "",
      working_days_requested: 1,
      status: "pending",
      approved: false,
      notes: "",
    });
    const requestEditingId = ref(null);
    const requestRangeMenu = ref(false);
    const requestRangeActiveEdge = ref("start");

    const balanceDialog = ref(false);
    const balanceDialogLoading = ref(false);
    const balanceDialogMode = ref("update");
    const balanceForm = ref({
      annual_leave_days: 0,
      accrual_start_date: "",
      accrual_finish_date: "",
      carryover_days: 0,
      total_accrued_days: 0,
      total_taken_days: 0,
      remaining_days: 0,
      notes: "",
    });

    const allowHolidayRequests = computed(() => true);

    const overviewVisibleLimit = ref(OVERVIEW_INITIAL_VISIBLE_REQUESTS);
    const showAllHolidayRequests = ref(false);
    const todayIso = formatAsIsoDate(new Date());
    const overviewCalendarSelection = ref([]);
    const initialOverviewDate = toDateObject(todayIso);
    if (initialOverviewDate) {
      overviewCalendarSelection.value = [initialOverviewDate];
    }
    const userRole = ref(localStorage.getItem("user-role") || "");
    const storedRoleKeys = ref(
      (() => {
        try {
          const raw = localStorage.getItem("user-roles");
          const parsed = raw ? JSON.parse(raw) : [];
          return Array.isArray(parsed)
            ? parsed.map((r) => String(r?.key || r?.role || r || "").toLowerCase()).filter(Boolean)
            : [];
        } catch (e) {
          return [];
        }
      })()
    );
    const userRoleSet = computed(() => {
      const set = new Set();
      const primary = normalizeRoleKey(userRole.value);
      if (primary) {
        expandRoleAliases(primary).forEach((alias) => set.add(alias));
      }
      storedRoleKeys.value.forEach((rk) => {
        expandRoleAliases(normalizeRoleKey(rk)).forEach((alias) => set.add(alias));
      });
      return set;
    });
    const subordinateBalances = ref({});
    const PRIVILEGED_BALANCE_ROLES = ["vacation-manager", "vacation-director", "vacation-hr", "admin", "miniadmin"];

    const effectiveUserRole = computed(() => {
      const stored = String(userRole.value || "").trim().toLowerCase();
      const employeeRole = String(employee.value?.role || "").trim().toLowerCase();
      if (stored && stored !== "user") {
        return stored;
      }
      if (stored === "user" && PRIVILEGED_BALANCE_ROLES.includes(employeeRole)) {
        return employeeRole || stored;
      }
      if (!stored && employeeRole) {
        return employeeRole;
      }
      return stored || employeeRole || "";
    });

    const employeeUserAccount = ref(null);
    const userLookupLoading = ref(false);
    const createUserDialog = ref(false);
    const userDialogLoading = ref(false);
    const inviteLoading = ref(false);
    const userDialogMode = ref("create");
    const userForm = ref({
      id: null,
      email: "",
      password: "",
      name: "",
      role: "",
    });
    const timesheetAutoPrompt = ref(false);

    const requestHeaders = computed(() => [
      { key: "holiday_type_name", title: t("employeeDetails.tables.requests.type") },
      { key: "period", title: t("employeeDetails.tables.requests.period") },
      { key: "working_days_requested", title: t("employeeDetails.tables.requests.days") },
      { key: "status", title: t("employeeDetails.tables.requests.status") },
      { key: "approved_by_name", title: t("employeeDetails.tables.requests.approvedBy") },
      { key: "approved_at", title: t("employeeDetails.tables.requests.approvedAt") },
      { key: "actions", title: t("employeeDetails.tables.requests.actions"), sortable: false },
    ]);

   const historyHeaders = computed(() => [
     { key: "holiday_type_name", title: t("employeeDetails.tables.history.type") },
     { key: "start_date", title: t("employeeDetails.tables.history.start") },
     { key: "end_date", title: t("employeeDetails.tables.history.end") },
     { key: "working_days_requested", title: t("employeeDetails.tables.history.days") },
     { key: "status", title: t("employeeDetails.tables.history.status") },
     { key: "created_at", title: t("employeeDetails.tables.history.created") },
   ]);

    const statusMapping = computed(() => ({
      pending: { label: t("employeeDetails.statuses.pending"), color: "warning" },
      approved: { label: t("employeeDetails.statuses.approved"), color: "success" },
      rejected: { label: t("employeeDetails.statuses.rejected"), color: "error" },
      cancelled: { label: t("employeeDetails.statuses.cancelled"), color: "neutral" },
      draft: { label: t("employeeDetails.statuses.draft"), color: "neutral" },
      cancelPending: { label: t("employeeDetails.statuses.cancelPending"), color: "error" },
    }));

    const statusOptions = computed(() =>
      Object.entries(statusMapping.value)
        .filter(([key]) => key !== "cancelPending")
        .map(([value, meta]) => ({ value, title: meta.label }))
    );

    const rules = {
      required: (value) => !!value || t("employeeDetails.form.required"),
    };

    const userDialogTitle = computed(() =>
      userDialogMode.value === "create"
        ? t("employeeDetails.userDialog.createTitle")
        : t("employeeDetails.userDialog.editTitle")
    );

    const userDialogSubmitLabel = computed(() =>
      userDialogMode.value === "create"
        ? t("employeeDetails.userDialog.submitCreate")
        : t("employeeDetails.userDialog.submitEdit")
    );

    const inviteButtonLabel = computed(() =>
      userDialogMode.value === "create"
        ? t("employeeDetails.userDialog.inviteCreate")
        : t("employeeDetails.userDialog.inviteEdit")
    );

    const canSendInvite = computed(() =>
      isInviteReady({
        email: userForm.value?.email,
        role: userForm.value?.role,
        mode: userDialogMode.value,
        userId: userForm.value?.id,
      })
    );

    const initials = computed(() => {
      const fn = employee.value.first_name?.[0] || "";
      const ln = employee.value.last_name?.[0] || "";
      return `${fn}${ln}`.toUpperCase() || "?";
    });

    const fullName = computed(() => `${employee.value.first_name || ''} ${employee.value.last_name || ''}`.trim());

    const personalInfo = computed(() => [
      {
        label: t("employeeDetails.personal.cnp"),
        value: employee.value.cnp || "—",
        icon: "mdi-card-account-details-outline",
      },
      {
        label: t("employeeDetails.personal.birthDate"),
        value: formatDisplayDate(employee.value.birth_date) || "—",
        icon: "mdi-cake-variant-outline",
      },
      {
        label: t("employeeDetails.personal.gender"),
        value: employee.value.gender || "—",
        icon: "mdi-gender-male-female",
      },
      {
        label: t("employeeDetails.personal.nationality"),
        value: employee.value.nationality || "—",
        icon: "mdi-earth",
      },
      {
        label: t("employeeDetails.personal.maritalStatus"),
        value: employee.value.marital_status || "—",
        icon: "mdi-ring",
      },
      {
        label: t("employeeDetails.personal.phone"),
        value: employee.value.phone || "—",
        icon: "mdi-phone-outline",
        href: /\d/.test(employee.value.phone || "") ? `tel:${String(employee.value.phone).replace(/[^+\d]/g, "")}` : null,
      },
      {
        label: t("employeeDetails.personal.email"),
        value: employee.value.email || "—",
        icon: "mdi-email-outline",
        href: employee.value.email ? `mailto:${employee.value.email}` : null,
      },
      {
        label: t("employeeDetails.personal.address"),
        value: employee.value.address || "—",
        icon: "mdi-map-marker-outline",
      },
    ]);

    const professionalInfo = computed(() => [
      {
        label: t("employeeDetails.professional.company"),
        value: employee.value.company_name || "—",
        icon: "mdi-office-building-outline",
      },
      {
        label: t("employeeDetails.professional.department"),
        value: employee.value.department || "—",
        icon: "mdi-account-group-outline",
      },
      {
        label: t("employeeDetails.professional.contractType"),
        value: employee.value.type_of_contract || "—",
        icon: "mdi-briefcase-outline",
      },
      {
        label: t("employeeDetails.professional.salary"),
        value: formatDisplayAmount(employee.value.salary) || "—",
        icon: "mdi-cash",
      },
      {
        label: t("employeeDetails.professional.hireDate"),
        value: formatDisplayDate(employee.value.hire_date) || "—",
        icon: "mdi-calendar-start",
      },
      {
        label: t("employeeDetails.professional.releaseDate"),
        value: formatDisplayDate(employee.value.release_date) || "—",
        icon: "mdi-calendar-end",
      },
    ]);

    const holidayTypeOptions = computed(() =>
      holidayTypes.value.map((type) => ({
        title: `${type.name_ro}${Number(type.is_paid) ? ' • plătit' : ''}`,
        value: type.id,
      }))
    );

    const holidayTypeMap = computed(() => {
      const map = new Map();
      holidayTypes.value.forEach((type) => {
        map.set(Number(type.id), type);
      });
      return map;
    });

    const companyOptions = computed(() =>
      companiesList.value.map((company) => ({ title: company.company_name, value: company.id }))
    );

    const formOptions = computed(() => buildEmployeeFormOptions(t));
    const employeeStatusOptions = computed(() => formOptions.value.status);
    const contractTypeOptions = computed(() => formOptions.value.contractType);
    const genderOptions = computed(() => formOptions.value.gender);
    const nationalityOptions = computed(() => formOptions.value.nationality);
    const maritalStatusOptions = computed(() => formOptions.value.maritalStatus);

    const employeeRoleOptions = computed(() => {
      const baseOptions = [...formOptions.value.role];
      const currentRole = (() => {
        if (editEmployee.value?.role) return String(editEmployee.value.role).trim();
        if (employee.value?.role) return String(employee.value.role).trim();
        return "";
      })();

      if (currentRole && !baseOptions.some((option) => option.value === currentRole)) {
        baseOptions.push({
          title: currentRole.charAt(0).toUpperCase() + currentRole.slice(1),
          value: currentRole,
        });
      }

      return baseOptions;
    });

    const managerOptions = computed(() => {
      const companyId = Number(editEmployee.value.company_id || employee.value.company_id);
      if (!companyId) return [];
      return employeesList.value
        .filter((emp) => Number(emp.company_id) === companyId && emp.id !== employeeId.value)
        .map((emp) => ({ title: `${emp.first_name} ${emp.last_name}`.trim(), value: emp.id }));
    });

    const currentHolidayType = computed(() =>
      holidayTypeMap.value.get(Number(requestForm.value.holiday_type_id)) || null
    );

    const currentTypeReducesBalance = computed(() => isAnnualLeaveType(currentHolidayType.value));

    const sortedHolidayRequests = computed(() => {
      const now = new Date();
      const upcoming = [];
      const past = [];

      holidayRequests.value.forEach((request) => {
        const endDate = new Date(request.end_date || request.start_date || request.created_at || Date.now());
        if (Number.isNaN(endDate.getTime())) {
          upcoming.push(request);
          return;
        }
        if (endDate >= now) {
          upcoming.push(request);
        } else {
          past.push(request);
        }
      });

      upcoming.sort((a, b) => {
        const aDate = new Date(a.start_date || a.created_at || 0).getTime();
        const bDate = new Date(b.start_date || b.created_at || 0).getTime();
        return aDate - bDate;
      });

      past.sort((a, b) => {
        const aDate = new Date(a.start_date || a.created_at || 0).getTime();
        const bDate = new Date(b.start_date || b.created_at || 0).getTime();
        return bDate - aDate;
      });

      return [...upcoming, ...past];
    });

    const visibleHolidayRequestsOverview = computed(() =>
      showAllHolidayRequests.value
        ? sortedHolidayRequests.value
        : sortedHolidayRequests.value.slice(0, Math.max(0, overviewVisibleLimit.value))
    );

    const hasMoreHolidayRequests = computed(
      () =>
        !showAllHolidayRequests.value && sortedHolidayRequests.value.length > overviewVisibleLimit.value
    );

    const showMoreHolidayRequests = () => {
      if (showAllHolidayRequests.value) {
        return;
      }
      overviewVisibleLimit.value += OVERVIEW_LOAD_MORE_STEP;
    };

    const toggleShowAllHolidayRequests = () => {
      showAllHolidayRequests.value = !showAllHolidayRequests.value;
      if (!showAllHolidayRequests.value) {
        overviewVisibleLimit.value = OVERVIEW_INITIAL_VISIBLE_REQUESTS;
      }
    };

    watch(
      () => editEmployee.value?.company_id,
      (newVal, oldVal) => {
        if ((oldVal === null || oldVal === undefined) || newVal === oldVal) {
          return;
        }
        if (editEmployee.value) {
          editEmployee.value.id_manager = null;
        }
      }
    );

    watch(sortedHolidayRequests, () => {
      if (!showAllHolidayRequests.value) {
        overviewVisibleLimit.value = OVERVIEW_INITIAL_VISIBLE_REQUESTS;
      }
    });

    const subordinateEmployees = computed(() =>
      employeesList.value.filter((emp) => Number(emp.id_manager) === employeeId.value)
    );

    const canViewSubordinateBalances = computed(() => {
      return PRIVILEGED_BALANCE_ROLES.includes(effectiveUserRole.value);
    });

    const subordinatePendingRequests = computed(() =>
      holidayRequests.value.filter(
        (req) => req.status === "pending" && subordinateEmployees.value.some((emp) => emp.id === req.employee_id)
      )
    );

    const statistics = computed(() => {
      const totalAccrued = displayBalance.value.totalAccrued;
      const totalTaken = totalTakenDisplay.value;
      const remaining = Number(holidayBalance.value?.remaining_days || 0);
      const currentYear = new Date().getFullYear();
      const approvedDaysYTD = holidayRequests.value
        .filter((req) => req.status === "approved" && new Date(req.start_date).getFullYear() === currentYear)
        .reduce((sum, req) => sum + getWorkingDays(req), 0);

      const byTypeMap = new Map();
      holidayRequests.value
        .filter((req) => req.status === "approved")
        .forEach((req) => {
          const key = req.holiday_type_id;
          const existing = byTypeMap.get(key) || { days: 0, label: lookupHolidayType(key) || 'Tip necunoscut' };
          existing.days += getWorkingDays(req);
          byTypeMap.set(key, existing);
        });

      const byType = Array.from(byTypeMap.entries()).map(([type, data]) => ({
        type,
        label: data.label,
        days: data.days,
        percent: totalTaken ? (data.days / totalTaken) * 100 : 0,
      }));

      return {
        totalAccrued,
        remainingDays: remaining,
        approvedDaysYTD,
        usagePercent: totalAccrued ? (totalTaken / totalAccrued) * 100 : 0,
        byType,
      };
    });

    const canManageRequests = computed(() => APPROVER_ROLES.includes(userRole.value));
    const canEditApprovedRequest = computed(() =>
      ["admin", "miniadmin", "vacation-hr", "hr"].some((roleKey) => userRoleSet.value.has(roleKey))
    );

    const navigateToEmployee = (id) => {
      const targetId = parseEmployeeId(id);
      if (!targetId) {
        return;
      }
      if (props.inlineNavigation) {
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("ext-dashboard:open-subordinate", {
              detail: { employeeId: targetId },
            })
          );
        }
        return;
      }
      router.push({ name: "EmployeeDetails", params: { id: targetId } });
    };

    const goToSubordinateApprovals = (id) => {
      navigateToEmployee(id);
    };

    const openSubordinateDetails = (id) => {
      navigateToEmployee(id);
    };

    function lookupHolidayType(id) {
      const type = holidayTypes.value.find((t) => Number(t.id) === Number(id));
      return type ? type.name_ro : null;
    }

    function isPastHolidayRequest(request) {
      const end = new Date(request?.end_date || request?.start_date || request?.created_at || Date.now());
      const today = new Date();
      end.setHours(23, 59, 59, 999);
      return end < today;
    }

    function statusLabel(status, pendingAction = null) {
      if (status === "pending" && pendingAction === "cancel") {
        return statusMapping.value.cancelPending?.label || statusMapping.value.pending?.label || status;
      }
      return statusMapping.value[status]?.label || status;
    }

    function statusChipColor(status, pendingAction = null) {
      if (status === "pending" && pendingAction === "cancel") {
        return statusMapping.value.cancelPending?.color || "error";
      }
      return statusMapping.value[status]?.color || "neutral";
    }

    const nonWorkingSet = computed(() => {
      const set = new Set();
      nonWorkingDays.value.forEach((entry) => {
        const iso = normalizeDateString(entry?.occurrence_date || entry?.date);
        if (iso) {
          set.add(iso);
        }
      });
      return set;
    });

    const eventColorByEvent = (event) => event?.color || "primary";

    const overviewCalendarEvents = computed(() => {
      const days = new Map();
      const nonWorkingIsoSet = new Set();

      const ensureDay = (iso) => {
        const bucket = days.get(iso) || { events: [], kinds: new Set() };
        if (!days.has(iso)) {
          days.set(iso, bucket);
        }
        return bucket;
      };

      nonWorkingDays.value.forEach((entry) => {
        const iso = normalizeDateString(entry?.occurrence_date || entry?.date);
        const date = toDateObject(iso);
        if (!iso || !date) return;
        nonWorkingIsoSet.add(iso);
        const event = {
          start: date,
          end: date,
          color: "neutral",
          title: entry?.name || t("employeeDetails.calendar.legend.nonWorking"),
          allDay: true,
          meta: { kind: "non-working", iso, highlightKind: null },
        };
        const bucket = ensureDay(iso);
        bucket.events.push(event);
        bucket.kinds.add("non-working");
      });

      const shortDaySet = buildShortDaySetFromNonWorking(nonWorkingDays.value);
      shortDaySet.forEach((iso) => {
        if (!iso || nonWorkingIsoSet.has(iso)) return;
        const date = toDateObject(iso);
        if (!date) return;
        const event = {
          start: date,
          end: date,
          color: "info",
          title: t("employeeDetails.calendar.legend.shortDay"),
          allDay: true,
          meta: { kind: "short-day", iso, highlightKind: null },
        };
        const bucket = ensureDay(iso);
        bucket.events.push(event);
        bucket.kinds.add("short-day");
      });

      holidayRequests.value
        .filter((req) => DISPLAYED_CALENDAR_STATUSES.has(req.status))
        .forEach((req) => {
          const startDate = toDateObject(req.start_date);
          const endDate = toDateObject(req.end_date || req.start_date) || startDate;
          if (!startDate) return;

          const chipColor = statusChipColor(req.status, req.pending_action);
          const themedColor = chipColor || "primary";
          const kind = req.status === "pending" ? "pending" : "approved";
          const title =
            req.holiday_type_name ||
            lookupHolidayType(req.holiday_type_id) ||
            statusLabel(req.status, req.pending_action);

          const cursor = new Date(startDate);
          while (cursor <= endDate) {
            const iso = formatAsIsoDate(cursor);
            if (!iso) break;
            const event = {
              start: new Date(cursor),
              end: new Date(cursor),
              color: themedColor,
              title,
              allDay: true,
              meta: { kind, iso, highlightKind: null },
            };
            const bucket = ensureDay(iso);
            bucket.events.push(event);
            bucket.kinds.add(kind);
            cursor.setDate(cursor.getDate() + 1);
          }
        });

      const result = [];
      days.forEach((bucket) => {
        const highlight = decideHighlight(bucket.kinds);
        if (bucket.events.length) {
          bucket.events[0].meta.highlightKind = highlight;
        }
        result.push(...bucket.events);
      });

      return result;
    });

    const onOverviewCalendarModelUpdate = (value) => {
      if (Array.isArray(value)) {
        const normalized = value
          .map((entry) => (entry instanceof Date ? entry : toDateObject(entry)))
          .filter(Boolean);
        overviewCalendarSelection.value = normalized;
      } else if (value) {
        const normalized = value instanceof Date ? value : toDateObject(value);
        overviewCalendarSelection.value = normalized ? [normalized] : [];
      } else {
        const today = toDateObject(todayIso);
        overviewCalendarSelection.value = today ? [today] : [];
      }
    };

    const handleRangePointerDown = (edge) => {
      requestRangeActiveEdge.value = edge;
    };

    const handleRangeFieldFocus = (edge) => {
      requestRangeActiveEdge.value = edge;
    };

    const handleRangeKeyboardOpen = (edge) => {
      requestRangeActiveEdge.value = edge;
      requestRangeMenu.value = true;
    };

    const handleRangeControlClick = (edge, event, menuProps) => {
      if (event && typeof event.preventDefault === "function") {
        event.preventDefault();
      }
      if (event && typeof event.stopPropagation === "function") {
        event.stopPropagation();
      }
      requestRangeActiveEdge.value = edge;
      requestRangeMenu.value = true;
      if (menuProps && typeof menuProps.onClick === "function") {
        menuProps.onClick(event);
      }
    };

    const recalculateTotalLeaveDays = () => {
      const total = computeAccruedUntilYearEnd(
        balanceForm.value.annual_leave_days,
        balanceForm.value.carryover_days,
        balanceForm.value.accrual_start_date,
        balanceForm.value.accrual_finish_date || holidayBalance.value?.accrual_finish_date || null
      );
      const currentTaken = toNumber(balanceForm.value.total_taken_days, 0);
      balanceForm.value.total_accrued_days = total;
      balanceForm.value.remaining_days = Number((total - currentTaken).toFixed(2));
    };

    const computeWorkingDays = (start, end) => {
      return computeWorkingDaysUtil(start, end, {
        nonWorkingDates: nonWorkingSet.value,
      });
    };

    function getWorkingDays(request) {
      if (!request) return 0;
      const recorded = toNumber(request.working_days_requested, 0);
      if (recorded) return recorded;
      const start = request.start_date ? formatDate(request.start_date) || request.start_date : "";
      const end = request.end_date ? formatDate(request.end_date) || request.end_date : "";
      return computeWorkingDays(start, end);
    }

    const hasApprovedOverlap = (startValue, endValue, excludeId = null) => {
      // Guard reactiv pe tip; nucleul de suprapunere e pur (employeeLeave.mjs).
      if (!currentTypeReducesBalance.value) return false;
      return hasApprovedAnnualLeaveOverlap({
        startValue,
        endValue,
        excludeId,
        holidayRequests: holidayRequests.value,
        holidayTypeMap: holidayTypeMap.value,
      });
    };

    const totalTakenDisplay = computed(() => {
      const fromBalance = toNumber(holidayBalance.value?.total_taken_days, 0);
      if (fromBalance > 0) return fromBalance;
      return holidayRequests.value
        .filter((req) => req.status === "approved")
        .filter((req) => isAnnualLeaveType(holidayTypeMap.value.get(Number(req.holiday_type_id))))
        .reduce((sum, req) => sum + getWorkingDays(req), 0);
    });

    const displayBalance = computed(() => {
      const annual = toNumber(holidayBalance.value?.annual_leave_days, 0);
      const carry = toNumber(holidayBalance.value?.carryover_days, 0);
      const accrualStartRaw = holidayBalance.value?.accrual_start_date;
      const totalAccrued = computeAccruedUntilYearEnd(
        annual,
        carry,
        accrualStartRaw,
        holidayBalance.value?.accrual_finish_date || null
      );
      const totalTaken = totalTakenDisplay.value;
      const remaining = Number((totalAccrued - totalTaken).toFixed(2));
      return {
        annual,
        totalAccrued,
        totalTaken,
        remaining,
      };
    });

    const blockedDaysSet = computed(() =>
      buildBlockedAnnualLeaveDays({
        holidayRequests: holidayRequests.value,
        holidayTypeMap: holidayTypeMap.value,
        excludeId: requestEditingId.value,
      })
    );

    const calendarEvents = computed(() => {
      if (!currentTypeReducesBalance.value) return [];
      const map = new Map();
      nonWorkingSet.value.forEach((iso) => {
        const date = toDateObject(iso);
        if (date) {
          map.set(iso, { date, color: "warning" });
        }
      });
      blockedDaysSet.value.forEach((iso) => {
        const date = toDateObject(iso);
        if (date) {
          map.set(iso, { date, color: "error" });
        }
      });
      return Array.from(map.values());
    });

    const allowedCalendarDates = (value) => {
      const iso = normalizeDateString(value);
      if (!iso) return false;
      const finish = normalizeDateString(holidayBalance.value?.accrual_finish_date);
      if (finish && iso > finish) return false;
      if (!currentTypeReducesBalance.value) return true;
      if (nonWorkingSet.value.has(iso)) {
        if (requestEditingId.value) {
          const editing = holidayRequests.value.find((req) => Number(req.id) === Number(requestEditingId.value));
          if (editing) {
            const startIso = normalizeDateString(editing.start_date);
            const endIso = normalizeDateString(editing.end_date);
            if (startIso && endIso && startIso <= iso && iso <= endIso) {
              // permite păstrarea selecției existente, dar nu noi intervale.
              return true;
            }
          }
        }
        return false;
      }
      return !blockedDaysSet.value.has(iso);
    };

    const rangeDateModel = computed(() => {
      const start = toDateObject(requestForm.value.start_date);
      const end = toDateObject(requestForm.value.end_date);

      if (start && end) {
        if (start <= end) {
          return [start, end];
        }
        return [end, start];
      }

      if (start) {
        return [start];
      }

      return [];
    });

    const onCalendarRangeChange = (value) => {
      const selection = normalizeRangeSelection(value);
      const targetEdge = requestRangeActiveEdge.value || "start";
      const currentStart = requestForm.value.start_date;
      const currentEnd = requestForm.value.end_date;

      if (targetEdge === "end") {
        const candidate = selection.hasEnd
          ? selection.end
          : selection.hasStart
          ? selection.start
          : "";

        if (candidate) {
          if (currentStart && currentStart > candidate) {
            requestForm.value.start_date = candidate;
            requestForm.value.end_date = currentStart;
          } else {
            requestForm.value.end_date = candidate;
            if (!currentStart && selection.hasStart) {
              requestForm.value.start_date = selection.start;
            }
          }

          if (requestForm.value.start_date && requestForm.value.end_date) {
            requestRangeMenu.value = false;
          }
        }

        applyWorkingDays();
        return;
      }

      const candidateStart = selection.hasStart
        ? selection.start
        : selection.hasEnd
        ? selection.end
        : "";

      if (candidateStart) {
        if (selection.hasStart && selection.hasEnd) {
          const nextStart = selection.start;
          const nextEnd = selection.end;
          if (nextStart <= nextEnd) {
            requestForm.value.start_date = nextStart;
            requestForm.value.end_date = nextEnd;
          } else {
            requestForm.value.start_date = nextEnd;
            requestForm.value.end_date = nextStart;
          }
          requestRangeMenu.value = false;
        } else {
          requestForm.value.start_date = candidateStart;
          if (currentEnd && currentEnd < candidateStart) {
            requestForm.value.end_date = candidateStart;
          }
          if (!selection.hasEnd) {
            requestRangeActiveEdge.value = "end";
          }
        }
      }

      applyWorkingDays();
    };


    function applyWorkingDays() {
      requestForm.value.working_days_requested = computeWorkingDays(
        requestForm.value.start_date,
        requestForm.value.end_date
      );
    }

    function openRequestDialog(request = null) {
      if (!allowHolidayRequests.value) return;
      if (request?.status === "approved" && !canEditApprovedRequest.value) {
        return;
      }
      if (request) {
        requestEditingId.value = request.id;
        requestForm.value = {
          holiday_type_id: request.holiday_type_id,
          start_date: formatDate(request.start_date),
          end_date: formatDate(request.end_date),
          working_days_requested: Number(request.working_days_requested || 1),
          status: request.status,
          approved: Number(request.approved) === 1,
          notes: request.notes || "",
        };
      } else {
        requestEditingId.value = null;
        let today = new Date();
        let formatted = formatAsIsoDate(today);
        const finish = normalizeDateString(holidayBalance.value?.accrual_finish_date);
        if (finish) {
          const finishDate = toDateObject(finish);
          if (!finishDate || finishDate < today) {
            notify(t("employeeDetails.feedback.requestsClosedAfter", { date: finish }), "error");
            return;
          }
        }
        while (hasApprovedOverlap(formatted, formatted)) {
          today.setDate(today.getDate() + 1);
          formatted = formatAsIsoDate(today);
        }
        requestForm.value = {
          holiday_type_id: holidayTypeOptions.value[0]?.value || null,
          start_date: formatted,
          end_date: formatted,
          working_days_requested: 1,
          status: "pending",
          approved: false,
          notes: "",
        };
      }
      applyWorkingDays();
      requestRangeActiveEdge.value = "start";
      requestDialog.value = true;
    }

    watch(
      () => requestForm.value.approved,
      (approved) => {
        if (approved && requestForm.value.status !== "approved") {
          requestForm.value.status = "approved";
        }
      }
    );

    watch(
      () => requestForm.value.status,
      (status) => {
        if (status !== "approved" && requestForm.value.approved) {
          requestForm.value.approved = false;
        }
      }
    );

    watch(
      () => [
        balanceForm.value.annual_leave_days,
        balanceForm.value.carryover_days,
        balanceForm.value.accrual_start_date,
        balanceForm.value.accrual_finish_date,
        balanceForm.value.total_taken_days,
      ],
      () => {
        recalculateTotalLeaveDays();
      }
    );

    watch(balanceDialog, (isOpen) => {
      if (!isOpen) {
        balanceDialogMode.value = "update";
      }
    });

    function openBalanceDialog(mode = "update") {
      balanceDialogMode.value = mode;
      balanceForm.value = {
        annual_leave_days: holidayBalance.value?.annual_leave_days || 0,
        accrual_start_date: formatDate(holidayBalance.value?.accrual_start_date) || formatAsIsoDate(new Date()),
        accrual_finish_date: formatDate(holidayBalance.value?.accrual_finish_date) || "",
        carryover_days: holidayBalance.value?.carryover_days || 0,
        total_accrued_days: holidayBalance.value?.total_accrued_days || 0,
        total_taken_days:
          toNumber(holidayBalance.value?.total_taken_days, 0) || totalTakenDisplay.value,
        remaining_days: holidayBalance.value?.remaining_days || 0,
        notes: holidayBalance.value?.notes || "",
      };
      recalculateTotalLeaveDays();
      balanceDialog.value = true;
    }

    async function submitHolidayRequest() {
      if (!employee.value.company_id) {
        notify(t("employeeHolidays.errors.unknownCompany"), "error");
        return;
      }
      const start = requestForm.value.start_date;
      const end = requestForm.value.end_date;
      const finish = normalizeDateString(holidayBalance.value?.accrual_finish_date);
      if (finish && end && end > finish) {
        notify(t("employeeHolidays.errors.afterFinish", { date: finish }), "error");
        return;
      }
      if (hasApprovedOverlap(start, end, requestEditingId.value)) {
        notify(t("employeeHolidays.errors.overlap"), "error");
        return;
      }
      requestDialogLoading.value = true;
      applyWorkingDays();
      const payload = {
        company_id: employee.value.company_id,
        employee_id: employeeId.value,
      holiday_type_id: requestForm.value.holiday_type_id,
      start_date: requestForm.value.start_date,
      end_date: requestForm.value.end_date,
      working_days_requested: requestForm.value.working_days_requested,
      status: requestForm.value.status,
        approved: requestForm.value.approved,
        notes: requestForm.value.notes,
        requested_by_employee_id: employeeId.value,
      };

      try {
        let savedId = requestEditingId.value || null;
        if (requestEditingId.value) {
          await axios.put(`/holidays/requests/${requestEditingId.value}`, payload);
        } else {
          const { data } = await axios.post("/holidays/requests", payload);
          savedId = data?.id || null;
        }
        requestDialog.value = false;
        await fetchHolidayData();
        if (!companyPdfDisabled.value && savedId) {
          lastSavedRequestId.value = savedId;
          downloadPromptDialog.value = true;
        }
      } catch (error) {
        console.error("Eroare submitHolidayRequest:", error);
        const message = error?.response?.data?.message || t("employeeHolidays.errors.saveRequest");
        notify(message, "error");
      } finally {
        requestDialogLoading.value = false;
      }
    }

    async function downloadPdf(request, kind = "request") {
      if (companyPdfDisabled.value) {
        notify(t("employeeHolidays.errors.pdfDisabled"), "error");
        return;
      }
      const requestId = typeof request === "object" ? request?.id : request;
      if (!requestId) return;
      pdfDownloadLoadingId.value = `${kind}-${requestId}`;
      try {
        const url =
          kind === "order"
            ? `/holidays/requests/${requestId}/pdf-order`
            : `/holidays/requests/${requestId}/pdf-request`;
        const { data } = await axios.get(url, { responseType: "blob" });
        const blob = new Blob([data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `${kind === "order" ? "ordin" : "cerere"}-concediu-${requestId}.pdf`;
        link.click();
        window.URL.revokeObjectURL(link.href);
        notify(t("employeeHolidays.download.started"));
      } catch (error) {
        console.error("Eroare download PDF:", error);
        const message = error?.response?.data?.message || t("employeeHolidays.download.error");
        notify(message, "error");
      } finally {
        pdfDownloadLoadingId.value = null;
      }
    }

    const confirmDownloadAfterSave = async () => {
      downloadPromptDialog.value = false;
      if (lastSavedRequestId.value) {
        await downloadPdf(lastSavedRequestId.value, "request");
      }
    };

    function formatCurrencyNumber(value) {
      return Number(value || 0).toFixed(2);
    }

    async function submitBalance() {
      if (!employee.value.company_id) {
        balanceDialogError.value = t("employeeHolidays.errors.unknownCompany");
        return;
      }
      balanceDialogLoading.value = true;
      balanceDialogError.value = "";
      try {
        const payload = {
          company_id: employee.value.company_id,
          employee_id: employeeId.value,
          annual_leave_days: Number(balanceForm.value.annual_leave_days) || 0,
          accrual_start_date: balanceForm.value.accrual_start_date,
          accrual_finish_date: balanceForm.value.accrual_finish_date || null,
          carryover_days: Number(balanceForm.value.carryover_days) || 0,
          total_accrued_days: Number(balanceForm.value.total_accrued_days) || 0,
          total_taken_days: Number(balanceForm.value.total_taken_days) || 0,
          remaining_days: Number(balanceForm.value.remaining_days) || 0,
          notes: balanceForm.value.notes,
          skip_recalc: balanceDialogMode.value === 'edit' ? 1 : 0,
        };
        await axios.post("/holidays/balances", payload);
        balanceDialog.value = false;
        await fetchHolidayData();
        notify(t("employeeDetails.feedback.balanceSaved"));
      } catch (error) {
        console.error("Eroare submitBalance:", error);
        balanceDialogError.value = error?.response?.data?.message || t("employeeDetails.feedback.balanceSaveError");
      } finally {
        balanceDialogLoading.value = false;
        balanceForm.value._editMode = false;
      }
    }

    async function approveRequest(request) {
      if (!canManageRequests.value) return;
      if (!employee.value.id_manager) {
        notify(t("employeeDetails.feedback.managerRequiredApprove"), "error");
        return;
      }
      try {
        await axios.post(`/holidays/requests/${request.id}/approve`, {
          approved_by_employee_id: employee.value.id_manager || null,
          notes: request.notes || null,
        });
        await fetchHolidayData();
        notify(t("employeeDetails.feedback.requestApproved"));
      } catch (error) {
        console.error("Eroare approveRequest:", error);
        notify(t("employeeDetails.feedback.requestApproveError"), "error");
      }
    }

    async function rejectRequest(request) {
      if (!canManageRequests.value) return;
      if (!employee.value.id_manager) {
        notify(t("employeeDetails.feedback.managerRequiredReject"), "error");
        return;
      }
      try {
        await axios.post(`/holidays/requests/${request.id}/reject`, {
          approved_by_employee_id: employee.value.id_manager || null,
        });
        await fetchHolidayData();
        notify(t("employeeDetails.feedback.requestRejected"));
      } catch (error) {
        console.error("Eroare rejectRequest:", error);
        notify(t("employeeDetails.feedback.requestRejectError"), "error");
      }
    }

    async function cancelRequest(request) {
      if (!canManageRequests.value) return;
      try {
        await axios.post(`/holidays/requests/${request.id}/cancel`, {});
        await fetchHolidayData();
        notify(t("employeeDetails.feedback.requestCancelled"));
      } catch (error) {
        console.error("Eroare cancelRequest:", error);
        notify(t("employeeDetails.feedback.requestCancelError"), "error");
      }
    }

    async function fetchEmployee() {
      try {
        const response = await axios.get(`/candidati/employees/${employeeId.value}`);
        employee.value = response.data || {};
        await fetchEmployeeUserAccount();
      } catch (error) {
        console.error("Eroare la obținerea datelor angajatului:", error);
      }
    }

    async function fetchEmployeesList() {
      try {
        const response = await axios.get("/candidati/employees");
        employeesList.value = response.data || [];
      } catch (error) {
        console.error("Eroare la obținerea listei de angajați:", error);
      }
    }

    async function fetchCompanies() {
      try {
        const response = await axios.get("/candidati/companies");
        companiesList.value = response.data || [];
      } catch (error) {
        console.error("Eroare la obținerea companiilor:", error);
      }
    }

    const fetchEmployeeUserAccount = async () => {
      const email = sanitizeText(employee.value?.email);
      if (!email) {
        employeeUserAccount.value = null;
        return;
      }

      userLookupLoading.value = true;
      try {
        const { data } = await axios.get("/users", { params: { email } });
        const list = Array.isArray(data) ? data : [];
        const match = list.find(
          (user) => String(user?.email || "").toLowerCase() === String(email).toLowerCase()
        );
        employeeUserAccount.value = match || null;
      } catch (error) {
        console.error("Eroare la verificarea utilizatorului existent:", error);
        employeeUserAccount.value = null;
      } finally {
        userLookupLoading.value = false;
      }
    };

    async function fetchHolidayTypes() {
      try {
        const response = await axios.get("/holidays/types");
        holidayTypes.value = response.data || [];
      } catch (error) {
        console.error("Eroare fetchHolidayTypes:", error);
      }
    }

    async function fetchNonWorkingDays() {
      nonWorkingDays.value = [];
      if (!employee.value?.company_id) return;

      const now = new Date();
      let minDate = new Date(now.getFullYear(), 0, 1);
      let maxDate = new Date(now.getFullYear() + 1, 11, 31);

      holidayRequests.value.forEach((req) => {
        const startIso = normalizeDateString(req.start_date);
        const endIso = normalizeDateString(req.end_date);
        const startDate = startIso ? toDateObject(startIso) : null;
        const endDate = endIso ? toDateObject(endIso) : null;
        if (startDate && startDate < minDate) {
          minDate = new Date(startDate);
        }
        if (endDate && endDate > maxDate) {
          maxDate = new Date(endDate);
        }
      });

      try {
        const { data } = await axios.get("/holidays/non-working-days", {
          params: {
            company_id: employee.value.company_id,
            start_date: formatAsIsoDate(minDate),
            end_date: formatAsIsoDate(maxDate),
          },
        });

        nonWorkingDays.value = Array.isArray(data?.occurrences)
          ? data.occurrences
              .map((occ) => ({
                id: Number(occ.id),
                occurrence_date: normalizeDateString(occ.occurrence_date || occ.date),
                name: occ.name || "",
              }))
              .filter((occ) => occ.occurrence_date)
          : [];
      } catch (error) {
        console.error("Eroare fetchNonWorkingDays:", error);
        nonWorkingDays.value = [];
      }
    }

    async function fetchHolidayData() {
      if (!employee.value.company_id) return;
      loadingRequests.value = true;
      try {
        const [balanceRes, requestsRes] = await Promise.all([
          axios.get(`/holidays/balances`, {
            params: { company_id: employee.value.company_id, employee_id: employeeId.value },
          }),
          axios.get(`/holidays/requests`, {
            params: { company_id: employee.value.company_id, employee_id: employeeId.value },
          }),
        ]);
        holidayBalance.value = balanceRes.data?.[0] || null;
        holidayRequests.value = requestsRes.data || [];
        companyPdfDisabled.value = Boolean(requestsRes.data?.[0]?.company_pdfrequest);
        await fetchNonWorkingDays();
        await fetchSubordinateBalances();
      } catch (error) {
        console.error("Eroare fetchHolidayData:", error);
      } finally {
        loadingRequests.value = false;
      }
    }

    async function fetchSubordinateBalances() {
      if (!canViewSubordinateBalances.value) {
        subordinateBalances.value = {};
        return;
      }
      const companyId = employee.value.company_id;
      const subordinateIds = subordinateEmployees.value.map((emp) => Number(emp.id));
      if (!companyId || subordinateIds.length === 0) {
        subordinateBalances.value = {};
        return;
      }
      try {
        const { data } = await axios.get("/holidays/balances", {
          params: { company_id: companyId },
        });
        const idSet = new Set(subordinateIds);
        const map = {};
        (Array.isArray(data) ? data : []).forEach((row) => {
          if (idSet.has(Number(row.employee_id))) {
            map[row.employee_id] = row;
          }
        });
        subordinateBalances.value = map;
      } catch (error) {
        console.error("Eroare la obținerea soldurilor subordonaților:", error);
        subordinateBalances.value = {};
      }
    }

    async function fetchTimesheetSettings() {
      if (!employeeId.value) return;
      timesheetLoading.value = true;
      timesheetAutosaveEnabled.value = false;
      clearTimesheetAutosaveTimer();
      timesheetNotice.value = null;
      try {
        const { data } = await axios.get(`/timesheet/settings/${employeeId.value}`);
        timesheetSettings.value = toUiTimesheetSettings(data || {});
        timesheetAutosaveSignature.value = buildTimesheetPayloadSnapshot().signature;
      } catch (error) {
        console.error("Eroare fetchTimesheetSettings:", error);
        timesheetSettings.value = createDefaultTimesheetSettings();
        timesheetAutosaveSignature.value = buildTimesheetPayloadSnapshot().signature;
        timesheetNotice.value = {
          type: "error",
          text: t("employeeDetails.timesheet.noticeLoadError"),
        };
      } finally {
        timesheetAutosaveEnabled.value = true;
        timesheetLoading.value = false;
      }
    }

    async function saveTimesheetSettings(options = {}) {
      if (!employeeId.value) return;
      const { silentSuccess = false, payload: payloadOverride = null, signature: signatureOverride = "" } = options;
      const payload = payloadOverride || toServerTimesheetPayload(timesheetSettings.value);
      const payloadSignature = signatureOverride || JSON.stringify(payload);
      timesheetSaving.value = true;
      if (!silentSuccess) {
        timesheetNotice.value = null;
      }
      try {
        await axios.put(`/timesheet/settings/${employeeId.value}`, payload);
        timesheetAutosaveApplying.value = true;
        timesheetSettings.value = toUiTimesheetSettings({ ...payload });
        timesheetAutosaveSignature.value = payloadSignature;
        if (!silentSuccess) {
          timesheetNotice.value = {
            type: "success",
            text: t("employeeDetails.timesheet.noticeSaveSuccess"),
          };
        }
      } catch (error) {
        console.error("Eroare saveTimesheetSettings:", error);
        const message =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          t("employeeDetails.timesheet.noticeSaveError");
        timesheetNotice.value = { type: "error", text: message };
      } finally {
        timesheetAutosaveApplying.value = false;
        timesheetSaving.value = false;
      }
    }

    const scheduleTimesheetAutosave = () => {
      if (!timesheetAutosaveEnabled.value || timesheetLoading.value || timesheetAutosaveApplying.value) {
        return;
      }
      clearTimesheetAutosaveTimer();
      const snapshot = buildTimesheetPayloadSnapshot();
      if (snapshot.signature === timesheetAutosaveSignature.value) {
        return;
      }
      timesheetAutosaveTimer.value = setTimeout(async () => {
        timesheetAutosaveTimer.value = null;
        if (timesheetLoading.value || timesheetAutosaveApplying.value) {
          return;
        }
        const latestSnapshot = buildTimesheetPayloadSnapshot();
        if (latestSnapshot.signature === timesheetAutosaveSignature.value) {
          return;
        }
        await saveTimesheetSettings({
          silentSuccess: true,
          payload: latestSnapshot.payload,
          signature: latestSnapshot.signature,
        });
      }, 700);
    };

    // „Gata”: salvarea rămâne automată; la închidere se trimite imediat ce e în așteptare (fără a aștepta debounce-ul).
    async function closeTimesheetDrawer() {
      clearTimesheetAutosaveTimer();
      const snapshot = buildTimesheetPayloadSnapshot();
      if (snapshot.signature !== timesheetAutosaveSignature.value) {
        // O eroare veche de autosave nu trebuie să blocheze închiderea după o salvare reușită.
        timesheetNotice.value = null;
        await saveTimesheetSettings({ silentSuccess: true, payload: snapshot.payload, signature: snapshot.signature });
        // La eroare drawerul rămâne deschis cu mesajul inline (timesheetNotice).
        if (timesheetNotice.value?.type === "error") return;
        notify(t("employeeDetails.timesheet.noticeSaveSuccess"));
      }
      timesheetDrawer.value = false;
    }

    function openTimesheetFillDialog() {
      const defaults = buildDefaultFillRange();
      timesheetFillDialog.value = {
        open: true,
        loading: false,
        error: null,
        start_date: defaults.start_date,
        end_date: defaults.end_date,
      };
      timesheetFillActiveEdge.value = "start";
      timesheetFillRangeMenu.value = false;
    }

    async function confirmTimesheetFill() {
      const { start_date: startIso, end_date: endIso } = timesheetFillDialog.value;
      const startDate = toDateObject(startIso);
      const endDate = toDateObject(endIso);

      if (!startDate || !endDate) {
        timesheetFillDialog.value.error = t("employeeDetails.timesheet.fillInvalid");
        return;
      }

      let rangeStart = startDate;
      let rangeEnd = endDate;
      if (rangeEnd < rangeStart) {
        const temp = rangeStart;
        rangeStart = rangeEnd;
        rangeEnd = temp;
      }

      const normalizedStart = formatAsIsoDate(rangeStart);
      const normalizedEnd = formatAsIsoDate(rangeEnd);
      timesheetFillDialog.value.start_date = normalizedStart;
      timesheetFillDialog.value.end_date = normalizedEnd;
      timesheetFillDialog.value.loading = true;
      timesheetFillDialog.value.error = null;
      timesheetFillRangeMenu.value = false;

      try {
        const payload = {
          employee_id: employeeId.value,
          start_date: normalizedStart,
          end_date: normalizedEnd,
        };
        const { data } = await axios.post("/timesheet/fill-range", payload);
        const inserted = Number(data?.inserted_events || 0);
        const filledDays = Number(data?.filled_days || 0);
        const message = inserted
          ? t("employeeDetails.timesheet.fillSuccess", { events: inserted, days: filledDays })
          : t("employeeDetails.timesheet.fillNone");
        timesheetNotice.value = {
          type: inserted ? "success" : "info",
          text: message,
        };
        timesheetFillDialog.value.open = false;
      } catch (error) {
        console.error("Eroare completare pontaj:", error);
        const message = error?.response?.data?.error || t("employeeDetails.timesheet.noticeSaveError");
        timesheetFillDialog.value.error = message;
      } finally {
        timesheetFillDialog.value.loading = false;
      }
    }

    const timesheetFillRangeDisplay = computed(() => ({
      start: timesheetFillDialog.value.start_date || "",
      end: timesheetFillDialog.value.end_date || "",
    }));

    const timesheetFillRangeDateModel = computed(() => {
      const start = toDateObject(timesheetFillDialog.value.start_date);
      const end = toDateObject(timesheetFillDialog.value.end_date);

      if (start && end) {
        if (start <= end) {
          return [start, end];
        }
        return [end, start];
      }

      if (start) {
        return [start];
      }

      return [];
    });

    const onTimesheetFillRangeChange = (value) => {
      const selection = normalizeRangeSelection(value);
      const targetEdge = timesheetFillActiveEdge.value || "start";
      const currentStart = timesheetFillDialog.value.start_date;
      const currentEnd = timesheetFillDialog.value.end_date;

      if (targetEdge === "end") {
        const candidate = selection.hasEnd
          ? selection.end
          : selection.hasStart
          ? selection.start
          : "";

        if (candidate) {
          if (currentStart && currentStart > candidate) {
            timesheetFillDialog.value.start_date = candidate;
            timesheetFillDialog.value.end_date = currentStart;
          } else {
            timesheetFillDialog.value.end_date = candidate;
            if (!currentStart && selection.hasStart) {
              timesheetFillDialog.value.start_date = selection.start;
            }
          }

          if (timesheetFillDialog.value.start_date && timesheetFillDialog.value.end_date) {
            timesheetFillRangeMenu.value = false;
            timesheetFillActiveEdge.value = "start";
          }
        }
        return;
      }

      const candidateStart = selection.hasStart
        ? selection.start
        : selection.hasEnd
        ? selection.end
        : "";

      if (candidateStart) {
        if (selection.hasStart && selection.hasEnd) {
          const nextStart = selection.start;
          const nextEnd = selection.end;
          if (nextStart <= nextEnd) {
            timesheetFillDialog.value.start_date = nextStart;
            timesheetFillDialog.value.end_date = nextEnd;
          } else {
            timesheetFillDialog.value.start_date = nextEnd;
            timesheetFillDialog.value.end_date = nextStart;
          }
          timesheetFillRangeMenu.value = false;
          timesheetFillActiveEdge.value = "start";
        } else {
          timesheetFillDialog.value.start_date = candidateStart;
          if (currentEnd && currentEnd < candidateStart) {
            timesheetFillDialog.value.end_date = candidateStart;
          }
          if (!selection.hasEnd) {
            timesheetFillActiveEdge.value = "end";
          }
        }
      }
    };

    const handleTimesheetFillPointerDown = (edge) => {
      timesheetFillActiveEdge.value = edge;
    };

    const handleTimesheetFillFieldFocus = (edge) => {
      timesheetFillActiveEdge.value = edge;
    };

    const handleTimesheetFillKeyboardOpen = (edge) => {
      timesheetFillActiveEdge.value = edge;
      timesheetFillRangeMenu.value = true;
    };

    const handleTimesheetFillControlClick = (edge, event, menuProps) => {
      if (event && typeof event.preventDefault === "function") {
        event.preventDefault();
      }
      if (event && typeof event.stopPropagation === "function") {
        event.stopPropagation();
      }
      timesheetFillActiveEdge.value = edge;
      timesheetFillRangeMenu.value = true;
      if (menuProps && typeof menuProps.onClick === "function") {
        menuProps.onClick(event);
      }
    };

    function openEditDialog() {
      const source = employee.value || {};
      const form = createEmptyEmployeeForm();

      form.id = source.id ?? null;
      form.company_id = source.company_id !== undefined && source.company_id !== null ? Number(source.company_id) : null;
      form.first_name = source.first_name || "";
      form.last_name = source.last_name || "";
      form.birth_date = normalizeDateForInput(source.birth_date);
      form.hire_date = normalizeDateForInput(source.hire_date);
      form.release_date = normalizeDateForInput(source.release_date);
      form.available_co_days = formatNumericField(source.available_co_days);
      form.status = source.status !== undefined && source.status !== null ? Number(source.status) : 1;
      form.phone = source.phone || "";
      form.email = source.email || "";
      form.position = source.position || "";
      form.role = source.role || "";
      form.id_manager = source.id_manager !== undefined && source.id_manager !== null ? Number(source.id_manager) : null;
      form.department = source.department || "";
      form.salary = formatNumericField(source.salary);
      form.type_of_contract = source.type_of_contract || "";
      form.address = source.address || "";
      form.cnp = source.cnp || "";
      form.gender = source.gender || "";
      form.nationality = source.nationality || "";
      form.marital_status = source.marital_status || "";

      editEmployee.value = form;
      editDialog.value = true;
    }


    const resolveUserCompanyIdFromEmployee = () => {
      const employeeCompanyId = parseNumeric(employee.value?.company_id);
      if (employeeCompanyId) return employeeCompanyId;

      const editFormCompanyId = parseNumeric(editEmployee.value?.company_id);
      if (editFormCompanyId) return editFormCompanyId;

      const contextCompanyId = parseNumeric(props.companyId);
      if (contextCompanyId) return contextCompanyId;

      if (typeof window !== "undefined") {
        const hrSelectedCompanyId = parseNumeric(localStorage.getItem("hr-dashboard:selected-employee-company-id"));
        if (hrSelectedCompanyId) return hrSelectedCompanyId;

        const storedCompanyId = parseNumeric(localStorage.getItem("user-company-id"));
        if (storedCompanyId) return storedCompanyId;
      }

      return null;
    };

    const ensureUserCompanyId = () => {
      const resolvedCompanyId = resolveUserCompanyIdFromEmployee();
      if (resolvedCompanyId) return resolvedCompanyId;
      userDialogError.value = t("employeeDetails.feedback.userCompanyUnknown");
      return null;
    };

    const resetUserForm = () => {
      userForm.value = {
        id: null,
        email: "",
        password: "",
        name: "",
        role: "",
      };
    };

    const closeCreateUserDialog = () => {
      createUserDialog.value = false;
      userDialogMode.value = "create";
      resetUserForm();
    };

    const openCreateUserDialog = () => {
      userDialogMode.value = "create";
      userForm.value = {
        id: null,
        email: sanitizeText(employee.value?.email) || "",
        password: "",
        name: fullName.value || "",
        role: sanitizeText(employee.value?.role) || "",
      };
      createUserDialog.value = true;
    };

    const openUpdateUserDialog = () => {
      if (!employeeUserAccount.value) return;
      userDialogMode.value = "edit";
      userForm.value = {
        id: employeeUserAccount.value.id ?? null,
        email: sanitizeRequiredText(employeeUserAccount.value.email) || "",
        password: "",
        name: sanitizeText(employeeUserAccount.value.name) || fullName.value || "",
        role:
          sanitizeText(employeeUserAccount.value.role) ||
          sanitizeText(employee.value?.role) ||
          "",
      };
      createUserDialog.value = true;
    };

    const sendUserInvite = async () => {
      const emailValue = sanitizeRequiredText(userForm.value.email);
      const roleValue = sanitizeRequiredText(userForm.value.role);
      userDialogError.value = "";
      if (!emailValue) {
        userDialogError.value = t("employeeDetails.feedback.userEmailRequired");
        return;
      }
      if (!roleValue) {
        userDialogError.value = t("employeeDetails.feedback.userRoleRequired");
        return;
      }
      const resolvedCompanyId = ensureUserCompanyId();
      if (!resolvedCompanyId) return;

      const payload = {
        email: emailValue,
        name: sanitizeText(userForm.value.name),
        role: roleValue,
        company_id: resolvedCompanyId,
      };

      if (userDialogMode.value === "edit" && userForm.value?.id) {
        payload.userId = userForm.value.id;
      }

      inviteLoading.value = true;
      try {
        const { data } = await axios.post("/users/invite", payload);
        notify(data?.message || t("employeeDetails.feedback.inviteSent"));
        const wasCreating = userDialogMode.value === "create";
        await fetchEmployeeUserAccount();
        closeCreateUserDialog();
        if (wasCreating) {
          timesheetAutoPrompt.value = true;
        }
      } catch (error) {
        console.error("Eroare la trimiterea invitației utilizatorului:", error);
        userDialogError.value = error?.response?.data?.message || t("employeeDetails.feedback.inviteError");
      } finally {
        inviteLoading.value = false;
      }
    };

    const submitCreateUser = async () => {
      const emailValue = sanitizeRequiredText(userForm.value.email);
      const passwordValue = String(userForm.value.password || "").trim();
      const roleValue = sanitizeRequiredText(userForm.value.role);

      userDialogError.value = "";
      if (!emailValue) {
        userDialogError.value = t("employeeDetails.feedback.userEmailRequired");
        return;
      }

      if (!passwordValue) {
        userDialogError.value = t("employeeDetails.feedback.userPasswordRequired");
        return;
      }
      if (!roleValue) {
        userDialogError.value = t("employeeDetails.feedback.userRoleRequired");
        return;
      }
      const resolvedCompanyId = ensureUserCompanyId();
      if (!resolvedCompanyId) return;

      const payload = {
        email: emailValue,
        password: passwordValue,
        name: sanitizeText(userForm.value.name),
        role: roleValue,
        company_id: resolvedCompanyId,
      };

      userDialogLoading.value = true;
      try {
        const { data } = await axios.post("/users", payload);
        notify(data?.message || t("employeeDetails.feedback.userCreated"));
        const wasCreating = userDialogMode.value === "create";
        closeCreateUserDialog();
        await fetchEmployeeUserAccount();
        if (wasCreating) {
          timesheetAutoPrompt.value = true;
        }
      } catch (error) {
        console.error("Eroare la crearea utilizatorului:", error);
        userDialogError.value = error?.response?.data?.message || t("employeeDetails.feedback.userCreateError");
      } finally {
        userDialogLoading.value = false;
      }
    };

    const submitUpdateUser = async () => {
      userDialogError.value = "";
      if (!userForm.value?.id) {
        userDialogError.value = t("employeeDetails.feedback.userIdMissing");
        return;
      }

      const emailValue = sanitizeRequiredText(userForm.value.email);
      if (!emailValue) {
        userDialogError.value = t("employeeDetails.feedback.userEmailRequired");
        return;
      }
      const resolvedCompanyId = ensureUserCompanyId();
      if (!resolvedCompanyId) return;

      const payload = {
        email: emailValue,
        name: sanitizeText(userForm.value.name),
        role: sanitizeText(userForm.value.role),
        company_id: resolvedCompanyId,
      };

      const passwordValue = String(userForm.value.password || "").trim();
      if (passwordValue) {
        payload.password = passwordValue;
      }

      userDialogLoading.value = true;
      try {
        const { data } = await axios.put(`/users/${userForm.value.id}`, payload);
        notify(data?.message || t("employeeDetails.feedback.userUpdated"));
        closeCreateUserDialog();
        await fetchEmployeeUserAccount();
      } catch (error) {
        console.error("Eroare la actualizarea utilizatorului:", error);
        userDialogError.value = error?.response?.data?.message || t("employeeDetails.feedback.userUpdateError");
      } finally {
        userDialogLoading.value = false;
      }
    };

    const handleUserFormSubmit = async () => {
      if (userDialogMode.value === "edit") {
        await submitUpdateUser();
      } else {
        await submitCreateUser();
      }
    };

    const dismissTimesheetAutoPrompt = () => {
      timesheetAutoPrompt.value = false;
    };

    const confirmTimesheetAutoPrompt = () => {
      timesheetAutoPrompt.value = false;
      timesheetSettings.value = {
        ...timesheetSettings.value,
        use_automatic: true,
      };
    };

    async function updateEmployee() {
      editEmployeeError.value = "";
      const { valid } = (await editEmployeeFormRef.value?.validate?.()) ?? { valid: true };
      if (!valid) return;
      editEmployeeLoading.value = true;
      try {
        if (!editEmployee.value?.id) {
          editEmployeeError.value = t("employeeDetails.feedback.employeeIdMissing");
          return;
        }
        const payload = {
          id: editEmployee.value.id,
          company_id: parseNumeric(editEmployee.value.company_id),
          first_name: sanitizeRequiredText(editEmployee.value.first_name),
          last_name: sanitizeRequiredText(editEmployee.value.last_name),
          birth_date: editEmployee.value.birth_date || null,
          hire_date: editEmployee.value.hire_date || null,
          release_date: editEmployee.value.release_date || null,
          available_co_days: parseNumeric(editEmployee.value.available_co_days),
          status: editEmployee.value.status !== undefined && editEmployee.value.status !== null ? Number(editEmployee.value.status) : 1,
          phone: sanitizeText(editEmployee.value.phone),
          email: sanitizeText(editEmployee.value.email),
          position: sanitizeText(editEmployee.value.position),
          role: sanitizeText(editEmployee.value.role),
          id_manager: parseNumeric(editEmployee.value.id_manager),
          department: sanitizeText(editEmployee.value.department),
          salary: parseNumeric(editEmployee.value.salary),
          type_of_contract: sanitizeText(editEmployee.value.type_of_contract),
          address: sanitizeText(editEmployee.value.address),
          cnp: sanitizeText(editEmployee.value.cnp),
          gender: sanitizeText(editEmployee.value.gender),
          nationality: sanitizeText(editEmployee.value.nationality),
          marital_status: sanitizeText(editEmployee.value.marital_status),
        };

        await axios.put(`/candidati/employees/${editEmployee.value.id}`, payload);
        editDialog.value = false;
        notify(t("employeeDetails.feedback.employeeUpdated"));
        // Salvarea a reușit: o reîncărcare eșuată nu se raportează ca eroare de salvare în drawerul deja închis.
        await Promise.all([fetchEmployee(), fetchEmployeesList()]).catch((reloadError) => {
          console.error("Eroare la reîncărcarea angajatului:", reloadError);
        });
        editEmployee.value = createEmptyEmployeeForm();
      } catch (error) {
        console.error("Eroare la actualizarea angajatului:", error);
        editEmployeeError.value = error?.response?.data?.message || t("employeeDetails.feedback.employeeUpdateError");
      } finally {
        editEmployeeLoading.value = false;
      }
    }

    // Eroarea inline aparține unei deschideri a drawerului; închiderea (inclusiv cu Escape) resetează formularul de utilizator.
    watch(editDialog, (open) => {
      if (open) editEmployeeError.value = "";
    });
    watch(balanceDialog, (open) => {
      if (open) balanceDialogError.value = "";
    });
    watch(createUserDialog, (open) => {
      userDialogError.value = "";
      if (!open) closeCreateUserDialog();
    });

    const loadEmployeeContext = async () => {
      if (!employeeId.value) return;
      await Promise.all([
        fetchEmployee(),
        fetchEmployeesList(),
        fetchCompanies(),
        fetchHolidayTypes(),
      ]);
      await fetchTimesheetSettings();
      await fetchHolidayData();
      await fetchSubordinateBalances();
    };

    function resetEmployeeContext() {
      timesheetAutosaveEnabled.value = false;
      clearTimesheetAutosaveTimer();
      employee.value = {};
      holidayBalance.value = null;
      holidayRequests.value = [];
      timesheetSettings.value = createDefaultTimesheetSettings();
      timesheetAutosaveSignature.value = buildTimesheetPayloadSnapshot(timesheetSettings.value).signature;
      timesheetNotice.value = null;
      const defaults = buildDefaultFillRange();
      timesheetFillDialog.value = {
        open: false,
        loading: false,
        error: null,
        start_date: defaults.start_date,
        end_date: defaults.end_date,
      };
      timesheetFillActiveEdge.value = "start";
      timesheetFillRangeMenu.value = false;
    }

    const handleEmployeeChange = async (nextId) => {
      const parsedId = parseEmployeeId(nextId);
      if (!parsedId || parsedId === employeeId.value) {
        return;
      }
      employeeId.value = parsedId;
      resetEmployeeContext();
      await loadEmployeeContext();
    };

    onMounted(async () => {
      if (!employeeId.value) return;
      await loadEmployeeContext();
    });

    watch(
      () => props.employeeId,
      async (newId) => {
        if (newId === null || newId === undefined) {
          return;
        }
        await handleEmployeeChange(newId);
      }
    );

    watch(
      () => route.params.id,
      async (newId, oldId) => {
        if (props.employeeId !== null && props.employeeId !== undefined) {
          return;
        }
        if (!newId || newId === oldId) return;
        await handleEmployeeChange(newId);
      }
    );

    watch(
      () => employee.value.company_id,
      () => {
        fetchSubordinateBalances();
      }
    );

    watch(
      () => subordinateEmployees.value.map((emp) => emp.id).join(","),
      () => {
        fetchSubordinateBalances();
      }
    );

    watch(
      timesheetSettings,
      () => {
        scheduleTimesheetAutosave();
      },
      { deep: true }
    );

    watch(canViewSubordinateBalances, () => {
      fetchSubordinateBalances();
    });

    onBeforeUnmount(() => {
      clearTimesheetAutosaveTimer();
    });

    function getSubordinateRemainingDisplay(id) {
      const balance = subordinateBalances.value?.[id];
      if (!balance || balance.remaining_days === undefined || balance.remaining_days === null) {
        return "—";
      }
      return formatNumber(balance.remaining_days);
    }

    return {
      employeeId,
      employee,
      employeesList,
      companiesList,
      holidayTypes,
      holidayBalance,
      holidayRequests,
      timesheetSettings,
      smartTrackerIdleMinutes,
      smartTrackerPromptMinutes,
      timesheetLoading,
      timesheetSaving,
      timesheetDrawer,
      closeTimesheetDrawer,
      timesheetNotice,
      timesheetAutoPrompt,
      showHrTimesheet,
      timesheetFillDialog,
      timesheetFillRangeMenu,
      timesheetFillRangeDisplay,
      timesheetFillRangeDateModel,
      openTimesheetFillDialog,
      confirmTimesheetFill,
      onTimesheetFillRangeChange,
      handleTimesheetFillPointerDown,
      handleTimesheetFillFieldFocus,
      handleTimesheetFillKeyboardOpen,
      handleTimesheetFillControlClick,
      nonWorkingDays,
      loadingRequests,
      requestHeaders,
      historyHeaders,
      requestDialog,
      requestDialogLoading,
      companyPdfDisabled,
      pdfDownloadLoadingId,
      feedbackSnack,
      drawerMenuProps,
      downloadPromptDialog,
      requestForm,
      requestEditingId,
      allowHolidayRequests,
      balanceDialog,
      balanceDialogLoading,
      balanceDialogError,
      editEmployeeLoading,
      editEmployeeError,
      editEmployeeFormRef,
      userDialogError,
      balanceDialogMode,
      balanceForm,
      editDialog,
      editEmployee,
      rules,
      userRole,
      fullName,
      initials,
      personalInfo,
      professionalInfo,
      statusOptions,
      holidayTypeOptions,
      managerOptions,
      employeeRoleOptions,
      companyOptions,
      employeeStatusOptions,
      contractTypeOptions,
      genderOptions,
      nationalityOptions,
      maritalStatusOptions,
      subordinateEmployees,
      subordinatePendingRequests,
      subordinateBalances,
      canViewSubordinateBalances,
      getSubordinateRemainingDisplay,
      statistics,
      formatDisplayDate,
      formatDateTime,
      formatNumber,
      statusLabel,
      statusChipColor,
      lookupHolidayType,
      sortedHolidayRequests,
      openRequestDialog,
      openBalanceDialog,
      submitHolidayRequest,
      confirmDownloadAfterSave,
      downloadPdf,
      submitBalance,
      saveTimesheetSettings,
      approveRequest,
      rejectRequest,
      cancelRequest,
      fetchHolidayData,
      openEditDialog,
      employeeUserAccount,
      userLookupLoading,
      createUserDialog,
      userDialogLoading,
      userDialogMode,
      userDialogTitle,
      userDialogSubmitLabel,
      inviteButtonLabel,
      inviteLoading,
      canSendInvite,
      userForm,
      openCreateUserDialog,
      openUpdateUserDialog,
      closeCreateUserDialog,
      sendUserInvite,
      handleUserFormSubmit,
      dismissTimesheetAutoPrompt,
      confirmTimesheetAutoPrompt,
      updateEmployee,
      canManageRequests,
      canEditApprovedRequest,
      goToSubordinateApprovals,
      openSubordinateDetails,
      requestRangeMenu,
      requestRangeActiveEdge,
      allowedCalendarDates,
      calendarEvents,
      rangeDateModel,
      onCalendarRangeChange,
      handleRangePointerDown,
      handleRangeFieldFocus,
      handleRangeKeyboardOpen,
      handleRangeControlClick,
      getWorkingDays,
      totalTakenDisplay,
      displayBalance,
      visibleHolidayRequestsOverview,
      hasMoreHolidayRequests,
      showMoreHolidayRequests,
      showAllHolidayRequests,
      toggleShowAllHolidayRequests,
      overviewCalendarSelection,
      overviewCalendarEvents,
      eventColorByEvent,
      onOverviewCalendarModelUpdate,
      isPastHolidayRequest,
    };
  },
});
</script>

<style scoped>
.employee-details {
  max-width: 1400px;
  margin: 0 auto;
}
.hero-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.hero-left {
  display: flex;
  align-items: center;
  gap: 24px;
}
.hero-avatar {
  font-size: 28px;
  font-weight: 600;
}
.hero-name {
  font-weight: 600;
  color: var(--app-text-strong);
}
.hero-meta div {
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), 0.62);
}
.hero-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.column {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.info-title {
  font-weight: 600;
  font-size: 16px;
  align-items: center;
}
.timesheet-card {
  position: relative;
}
.timesheet-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.timesheet-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.timesheet-section__header {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.timesheet-section__hint {
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), 0.62);
}
.timesheet-switch-wrapper {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: var(--app-radius-base);
  padding: 8px;
  background-color: rgba(var(--v-theme-on-surface), 0.02);
}
.timesheet-switch-wrapper :deep(.v-selection-control) {
  min-height: 28px;
}
.timesheet-switch-wrapper :deep(.v-switch__track) {
  height: 18px;
}
.timesheet-switch-wrapper :deep(.v-switch__thumb) {
  width: 16px;
  height: 16px;
}
.timesheet-switch-label {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.timesheet-switch-title {
  font-weight: 600;
  color: var(--app-text-strong);
  font-size: 13px;
}
.timesheet-smart-tracker-settings :deep(.timesheet-switch--compact-label .v-label) {
  font-size: 12px;
  line-height: 1.2;
  white-space: nowrap;
}
.timesheet-time-grid {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 6px;
  row-gap: 16px;
}
@media (min-width: 960px) {
  .timesheet-time-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
.timesheet-actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}
.timesheet-actions .v-btn {
  height: auto;
  padding-block: 8px;
}
.timesheet-actions :deep(.v-btn__content) {
  white-space: normal;
  text-align: center;
}
.info-row {
  display: flex;
  align-items: center;
  font-size: 14px;
  margin-bottom: 6px;
  color: rgba(var(--v-theme-on-surface), 0.80);
}
.info-row span {
  margin-left: 6px;
}
.info-link {
  margin-left: 6px;
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
  text-decoration-color: rgba(var(--v-theme-primary), 0.3);
}
.info-link:hover {
  text-decoration-color: currentColor;
}
.info-link:focus-visible {
  outline: 2px solid var(--ncrm-color-primary-focus);
  outline-offset: 2px;
  border-radius: var(--app-radius-sm);
}
.balance-value {
  font-variant-numeric: tabular-nums;
}
.balance-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
}
.subordinate-card {
  padding: 12px;
  cursor: pointer;
  transition: border-color var(--app-transition-fast), background-color var(--app-transition-fast);
}
.subordinate-card:hover {
  border-color: var(--ncrm-color-primary-border);
  background-color: var(--ncrm-color-hover-soft);
}
.subordinate-card:focus-within {
  border-color: var(--ncrm-color-primary-border);
}
.subordinate-card__name {
  display: block;
  padding: 0;
  border: 0;
  background: none;
  color: var(--app-text-strong);
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.subordinate-card__name:focus-visible {
  outline: 2px solid var(--ncrm-color-primary-focus);
  outline-offset: 2px;
  border-radius: var(--app-radius-sm);
}
.subordinate-card__chevron {
  opacity: 0.45;
}
.subordinate-card:hover .subordinate-card__chevron {
  opacity: 1;
}
.vacation-requests-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.history-empty {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}
.history-empty__icon {
  opacity: 0.45;
}
.request-row {
  border: 1px solid var(--ncrm-color-border-default);
  border-radius: var(--app-radius-base);
  padding: 12px;
}
.request-row__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 4px;
}
.request-row__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}
.request-row__meta {
  display: flex;
  align-items: center;
  gap: 4px;
}
.request-row__downloads {
  display: flex;
  align-items: center;
  gap: 2px;
}
.request-row__title {
  display: flex;
  align-items: center;
  gap: 8px;
}
.request-row--past {
  opacity: 0.65;
}
/* Calendar styling aligned with EmployeeHolidays.vue */
.calendar-card {
  display: flex;
  flex-direction: column;
  margin-top: 16px;
}
.calendar-card :deep(.v-card-text) {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1 1 auto;
  width: 100%;
}
.calendar-wrapper {
  max-width: 520px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  flex: 1 1 auto;
  width: 100%;
}
.vacation-calendar {
  border-radius: var(--app-radius-base);
  flex: 1 1 auto;
  width: 100%;
  --v-theme-background: var(--v-theme-surface);
  --v-theme-surface-light: var(--v-theme-surface);
}
.calendar-event-chip {
  display: flex;
  align-items: center;
  gap: 6px;
}
.calendar-event-chip__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}
.calendar-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), 0.62);
  justify-content: center;
  align-items: center;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}
.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}
.legend-dot.approved {
  background-color: rgb(var(--v-theme-success));
}
.legend-dot.pending {
  background-color: rgb(var(--v-theme-warning));
}
.legend-dot.non-working {
  background-color: rgba(var(--v-theme-on-surface), 0.38);
}
.legend-dot.short-day {
  background-color: rgb(var(--v-theme-info));
}
.calendar-day-overlay {
  position: absolute;
  inset: 0;
  border-radius: var(--app-radius-sm);
  opacity: 0.18;
  pointer-events: none;
}
.calendar-day-overlay--approved {
  background-color: rgb(var(--v-theme-success));
}
.calendar-day-overlay--pending {
  background-color: rgb(var(--v-theme-warning));
}
.calendar-day-overlay--non-working {
  background-color: rgb(var(--v-theme-on-surface));
}
.calendar-day-overlay--short-day {
  background-color: rgb(var(--v-theme-info));
}
.calendar-card :deep(.v-date-picker) {
  width: 100%;
  max-width: 520px;
}
.calendar-card :deep(.v-calendar-month__days) {
  grid-auto-rows: 100px;
}
.calendar-card :deep(.v-calendar-month__day) {
  position: relative;
  border-radius: var(--app-radius-base);
  overflow: hidden;
  min-height: 50px;
  transition: background-color var(--app-transition-base), box-shadow var(--app-transition-base);
}
.calendar-card :deep(.v-calendar-weekly__day-label),
.calendar-card :deep(.v-calendar-weekly__day-content) {
  position: relative;
  z-index: 1;
}
.calendar-card :deep(.v-calendar-month__day:has(.calendar-day-overlay--approved) .v-calendar-weekly__day-label .v-btn__content) {
  color: rgb(var(--v-theme-success));
}
.calendar-card :deep(.v-calendar-month__day:has(.calendar-day-overlay--pending) .v-calendar-weekly__day-label .v-btn__content) {
  color: rgb(var(--v-theme-warning));
}
.calendar-card :deep(.v-calendar-month__day:has(.calendar-day-overlay--non-working) .v-calendar-weekly__day-label .v-btn__content) {
  color: rgba(var(--v-theme-on-surface), 0.5);
}
.calendar-card :deep(.v-calendar-month__day:has(.calendar-day-overlay--short-day) .v-calendar-weekly__day-label .v-btn__content) {
  color: rgb(var(--v-theme-info));
}
.calendar-card .v-date-picker {
  --v-date-picker-header-height: 64px;
}
.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
}
@media (max-width: 960px) {
  .hero-card {
    flex-direction: column;
    align-items: flex-start;
  }
  .hero-actions {
    justify-content: flex-start;
  }
}
</style>
